"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  acknowledgeAuthTokenInvalid,
  getAuthGuardState,
  getSupabaseBrowserClient,
  clearSupabaseAuthStorage,
  getCookieName,
  resetSupabaseClient,
  subscribeAuthGuard,
} from "@/lib/supabase/browser";
import {
  clearVerifiedUserCache,
} from "@/lib/auth/verifiedUserClient";
import { getBusinessSessionExpiredLoginPath, PATHS } from "@/lib/auth/paths";
import { isCustomerNearbyPublicAllowed } from "@/lib/routes/isCustomerNearbyPublic";
import AuthStateDebug from "@/components/debug/AuthStateDebug";
import { stopRealtime } from "@/lib/realtimeManager";
import {
  isLogoutRedirectInFlight,
  performLogout,
  resolveLogoutRedirect,
} from "@/lib/auth/logout";
import { normalizeNextPath } from "@/lib/auth/normalizeNextPath";
import { getPostLoginRedirect } from "@/lib/auth/redirects";
import { clearClientRedirectState, readClientRedirectState } from "@/lib/auth/clientRedirectState";
import {
  getAccountDeletedRedirectPath,
  isBlockedAccountStatus,
  normalizeAccountStatus,
} from "@/lib/accountDeletion/status";
import { shouldSuppressAuthUiReset } from "@/lib/auth/loginErrors";
import { normalizePublicUserRole } from "@/lib/auth/currentAccountContext";
import { resolveAvatarUrl } from "@/lib/avatarUrl";
import { normalizeAuthUser } from "@/lib/auth/normalizeAuthUser";
import { fetchJsonWithTimeout } from "@/lib/fetchWithTimeout";
import { withTimeout } from "@/lib/withTimeout";
import { logDataDiag } from "@/lib/dataDiagnostics";
import {
  consumeConfirmedPasswordSession,
  consumeExplicitLoginIntent,
  EXPLICIT_LOGIN_CONFIRMED_EVENT,
  setExplicitLoginCorrelationRequired,
} from "@/lib/auth/explicitLoginIntent";

const AuthContext = createContext({
  supabase: null,
  authStatus: "loading",
  authReadiness: "initializing",
  authReconciliationStatus: "idle",
  serverAuthAuthority: "unknown",
  explicitLogoutActive: false,
  terminalInvalidSessionActive: false,
  tokenInvalidSessionActive: false,
  authorityRevision: 0,
  authResumeStatus: "idle",
  authResumeRevision: 0,
  authResumeError: null,
  businessContextStatus: "idle",
  authInitialized: false,
  session: null,
  status: "loading",
  user: null,
  profile: null,
  business: null,
  accountContext: null,
  hasBusinessAccess: false,
  businessAccessContexts: [],
  role: null,
  error: null,
  lastAuthEvent: null,
  lastError: null,
  loadingUser: true,
  rateLimited: false,
  rateLimitUntil: 0,
  rateLimitMessage: null,
  refreshDisabledUntil: 0,
  refreshDisabledReason: null,
  authBusy: false,
  authAction: null,
  authAttemptId: 0,
  authActionStartedAt: 0,
  providerInstanceId: null,
  refreshProfile: async () => {},
  refreshBusinessProfile: async () => {},
  updateProfile: (nextProfile = null) => { void nextProfile; },
  logout: async (_options = {}) => {},
  beginAuthAttempt: () => 0,
  endAuthAttempt: () => false,
  resetAuthUiState: () => {},
  retryAuth: async () => {},
  retryAuthResume: async () => {},
  seedAuthState: () => {},
  supportModeActive: false,
});

const AUTH_HANDOFF_PARAM = "yb_auth_handoff";
const AUTH_FRESH_PARAM = "yb_auth_fresh";
const AUTH_BOOTSTRAP_DEADLINE_MS = 8000;
const AUTH_SESSION_STEP_TIMEOUT_MS = 3500;
const AUTH_SERVER_SNAPSHOT_TIMEOUT_MS = 3500;
const AUTH_RECONCILIATION_UNCERTAIN_COOLDOWN_MS = 5000;
const AUTH_RESUME_DEADLINE_MS = 20_000;
const AUTH_RESUME_STEP_TIMEOUT_MS = 8000;
const AUTH_RESUME_REFRESH_SKEW_MS = 60_000;
const AUTH_RESUME_SIGNAL_COALESCE_MS = 120;
const AUTH_RESUME_SHORT_BACKGROUND_GRACE_MS = 30_000;
const AUTH_RESUME_MAX_FRESH_AUTHORITY_AGE_MS = 5 * 60_000;
const AUTH_RESUME_TRANSIENT_RETRY_DELAYS_MS = [500, 1500];
const AUTH_BOOTSTRAP_TRANSIENT_RETRY_DELAYS_MS = [500, 1500];
const CUSTOMER_ROUTE_LOOP_DIAG_PREFIX = "[CUSTOMER_ROUTE_LOOP_DIAG]";
const CUSTOMER_ONBOARDING_NAV_DIAG_PREFIX = "[CUSTOMER_ONBOARDING_NAV_DIAG]";

function isPwaDeploymentRecoveryActive() {
  if (typeof window === "undefined") return false;
  try {
    return window.__ybPwaStartup?.isDeploymentRecoveryActive?.() === true;
  } catch {
    return false;
  }
}

function recordPwaSessionExpirySuppressed() {
  if (typeof window === "undefined") return;
  try {
    window.__ybPwaStartup?.recordSessionExpirySuppressed?.();
  } catch {}
}

const resolveRole = (profile, user = null, fallbackRole = null) => {
  return (
    normalizePublicUserRole(profile?.role) ||
    normalizePublicUserRole(user?.app_metadata?.role) ||
    normalizePublicUserRole(fallbackRole)
  );
};

const isPublicBusinessPath = (pathname) => {
  if (!pathname) return false;
  return (
    pathname === "/business" ||
    pathname === "/business/" ||
    pathname.startsWith("/business/about") ||
    pathname.startsWith("/business/pricing") ||
    pathname.startsWith("/business/faq") ||
    pathname.startsWith("/business/how-it-works") ||
    pathname.startsWith("/business/retailers") ||
    pathname.startsWith("/business/login")
  );
};

const isProtectedPath = (pathname) => {
  if (!pathname) return false;
  if (isPublicBusinessPath(pathname)) return false;
  if (pathname.startsWith("/business/")) return true;
  if (pathname.startsWith("/customer")) return true;
  if (pathname.startsWith("/account")) return true;
  if (pathname.startsWith("/checkout")) return true;
  if (pathname.startsWith("/orders")) return true;
  return false;
};

const isBootstrapOnboardingPath = (pathname) => {
  if (!pathname) return false;
  return (
    pathname === "/onboarding" ||
    pathname.startsWith("/onboarding/") ||
    pathname === "/business/onboarding" ||
    pathname.startsWith("/business/onboarding/") ||
    pathname === "/customer/onboarding" ||
    pathname.startsWith("/customer/onboarding/")
  );
};

const isProtectedCustomerPath = (pathname) => {
  if (!pathname) return false;
  if (
    pathname === "/customer/onboarding" ||
    pathname.startsWith("/customer/onboarding/")
  ) {
    return false;
  }
  return pathname === "/customer" || pathname.startsWith("/customer/");
};

function getCurrentIntendedPath(pathname) {
  if (typeof window === "undefined") return normalizeNextPath(pathname) || "/";
  return normalizeNextPath(`${window.location.pathname}${window.location.search}`) || pathname || "/";
}

function getSessionRecoveryLoginPath(pathname, { business = false } = {}) {
  const next = getCurrentIntendedPath(pathname);
  if (business || pathname?.startsWith("/business")) {
    return getBusinessSessionExpiredLoginPath({ next });
  }
  return `${PATHS.auth.customerLogin}?next=${encodeURIComponent(next)}`;
}

function logCustomerRouteLoopDiag(payload) {
  if (typeof window === "undefined") return;
  if (process.env.NEXT_PUBLIC_AUTH_DIAG !== "1") return;
  console.info(CUSTOMER_ROUTE_LOOP_DIAG_PREFIX, payload);
}

function logCustomerOnboardingNavDiag(payload) {
  if (typeof window === "undefined") return;
  if (process.env.NEXT_PUBLIC_AUTH_DIAG !== "1") return;
  console.info(CUSTOMER_ONBOARDING_NAV_DIAG_PREFIX, payload);
}

function logBusinessDashboardRedirectDiag(payload) {
  if (typeof window === "undefined") return;
  if (!window.location.pathname.startsWith("/business/dashboard")) return;
  if (process.env.NODE_ENV === "production" && process.env.NEXT_PUBLIC_AUTH_DIAG !== "1") return;
  console.info("[BUSINESS_DASHBOARD_REDIRECT_DIAG]", payload);
}

function logExpiredSessionRedirectOwner({ owner, trigger, tokenInvalid = false }) {
  if (typeof window === "undefined") return;
  console.info("[AUTH_EXPIRED_SESSION_REDIRECT]", {
    owner,
    trigger,
    authority: authStore.state.serverAuthAuthority,
    reconciliation: authStore.state.authReconciliationStatus,
    authorityRevision: authStore.state.authorityRevision,
    explicitLogout: isAuthNavigationSuppressed(),
    tokenInvalid: tokenInvalid || Boolean(authStore.state.tokenInvalidAt),
    timestamp: Date.now(),
  });
}

function getCurrentSearchParamKeys() {
  if (typeof window === "undefined") return [];
  try {
    return Array.from(new URL(window.location.href).searchParams.keys());
  } catch {
    return [];
  }
}

const isAuthCallbackPath = (pathname) => {
  if (!pathname) return false;
  return (
    pathname === "/auth/callback" ||
    pathname.startsWith("/auth/callback/") ||
    pathname === "/auth/confirm" ||
    pathname.startsWith("/auth/confirm/") ||
    pathname === "/auth/verify" ||
    pathname.startsWith("/auth/verify/") ||
    pathname === "/business-auth" ||
    pathname.startsWith("/business-auth/")
  );
};

const isPublicLoginSurfacePath = (pathname) => {
  if (!pathname) return false;
  return (
    pathname === "/" ||
    pathname === "/signin" ||
    pathname === "/login" ||
    pathname.startsWith("/business-auth") ||
    pathname.startsWith("/business/login")
  );
};

const isBusinessCreatePasswordPath = (pathname) => {
  if (!pathname) return false;
  return (
    pathname === PATHS.auth.businessCreatePassword ||
    pathname.startsWith(`${PATHS.auth.businessCreatePassword}/`)
  );
};

const isBusinessPostConfirmPath = (pathname) => {
  if (!pathname) return false;
  return (
    pathname === PATHS.auth.businessPostConfirm ||
    pathname.startsWith(`${PATHS.auth.businessPostConfirm}/`)
  );
};

const buildSignedOutState = () => ({
  authStatus: "unauthenticated",
  authReadiness: "ready",
  authReconciliationStatus: "idle",
  serverAuthAuthority: "unknown",
  authResumeStatus: "idle",
  authResumeError: null,
  businessContextStatus: "idle",
  session: null,
  user: null,
  profile: null,
  business: null,
  accountContext: null,
  role: null,
  error: null,
});

const buildSignedInState = ({ user, profile, business, accountContext }) => ({
  authStatus: "authenticated",
  authReadiness: "ready",
  authReconciliationStatus: "idle",
  serverAuthAuthority: "authenticated",
  user: user ?? null,
  profile: profile ?? null,
  business: business ?? null,
  accountContext: accountContext ?? null,
  role: resolveRole(profile, user, null),
  error: null,
});

function mergeAuthUser(prevUser, nextUser) {
  const normalizedNextUser = normalizeAuthUser(nextUser);
  if (!normalizedNextUser) return normalizedNextUser ?? null;
  const normalizedPrevUser = normalizeAuthUser(prevUser);
  if (!normalizedPrevUser || normalizedPrevUser.id !== normalizedNextUser.id) {
    return normalizedNextUser;
  }
  return normalizeAuthUser({
    ...normalizedPrevUser,
    ...normalizedNextUser,
    app_metadata: {
      ...(normalizedPrevUser.app_metadata || {}),
      ...(normalizedNextUser.app_metadata || {}),
    },
    user_metadata: {
      ...(normalizedPrevUser.user_metadata || {}),
      ...(normalizedNextUser.user_metadata || {}),
    },
  });
}

function mergeProfileAvatar(prevProfile, nextProfile, user) {
  if (!nextProfile) return nextProfile ?? null;
  const nextAvatar = resolveAvatarUrl(nextProfile.profile_photo_url);
  if (nextAvatar) {
    return {
      ...nextProfile,
      profile_photo_url: nextAvatar,
    };
  }

  const priorAvatar = resolveAvatarUrl(
    prevProfile?.profile_photo_url,
    user?.user_metadata
  );
  if (!priorAvatar) return nextProfile;
  return {
    ...nextProfile,
    profile_photo_url: priorAvatar,
  };
}

function parseProfileUpdatedAt(profile) {
  const timestamp = Date.parse(profile?.updated_at || "");
  return Number.isFinite(timestamp) ? timestamp : null;
}

function hasProfileText(value) {
  return String(value || "").trim().length > 0;
}

function mergeFreshProfile(prevProfile, nextProfile, user) {
  if (!nextProfile) return prevProfile ?? null;
  if (!prevProfile?.id || prevProfile.id !== nextProfile.id) {
    return mergeProfileAvatar(prevProfile, nextProfile, user);
  }

  const prevUpdatedAt = parseProfileUpdatedAt(prevProfile);
  const nextUpdatedAt = parseProfileUpdatedAt(nextProfile);
  if (prevUpdatedAt !== null && nextUpdatedAt !== null && nextUpdatedAt < prevUpdatedAt) {
    return prevProfile;
  }

  const resolvedNext = mergeProfileAvatar(prevProfile, nextProfile, user);
  const mergedProfile = {
    ...prevProfile,
    ...resolvedNext,
  };

  if (prevUpdatedAt !== null && nextUpdatedAt === null) {
    for (const key of [
      "full_name",
      "phone",
      "address",
      "address_2",
      "city",
      "state",
      "postal_code",
      "email",
      "role",
    ]) {
      if (!hasProfileText(resolvedNext?.[key]) && hasProfileText(prevProfile?.[key])) {
        mergedProfile[key] = prevProfile[key];
      }
    }
  }

  return mergedProfile;
}

function areProfilesEqual(leftProfile, rightProfile) {
  return JSON.stringify(leftProfile || null) === JSON.stringify(rightProfile || null);
}

const withGuardState = (base) => ({
  ...base,
  rateLimited: authStore.state.rateLimited,
  rateLimitUntil: authStore.state.rateLimitUntil,
  rateLimitMessage: authStore.state.rateLimitMessage,
  tokenInvalidAt: authStore.state.tokenInvalidAt,
});

function createEmptyAuthState() {
  return {
    authStatus: "loading",
    authReadiness: "initializing",
    authReconciliationStatus: "idle",
    serverAuthAuthority: "unknown",
    authorityRevision: 0,
    authResumeStatus: "idle",
    authResumeRevision: 0,
    authResumeError: null,
    businessContextStatus: "idle",
    authInitialized: false,
    session: null,
    user: null,
    profile: null,
    business: null,
    accountContext: null,
    role: null,
    error: null,
    lastAuthEvent: null,
    lastError: null,
    rateLimited: false,
    rateLimitUntil: 0,
    rateLimitMessage: null,
    tokenInvalidAt: 0,
    refreshDisabledUntil: 0,
    refreshDisabledReason: null,
    authBusy: false,
    authAction: null,
    authAttemptId: 0,
    authActionStartedAt: 0,
    supportModeActive: false,
  };
}

function buildServerAuthSnapshot({
  initialUser,
  initialProfile,
  initialAccountContext,
  initialRole,
  initialAuthResolved,
  initialSupportModeActive,
}) {
  const snapshot = createEmptyAuthState();
  const user = normalizeAuthUser(initialUser);
  const profile = initialProfile
    ? mergeFreshProfile(null, initialProfile, user)
    : null;

  if (user?.id) {
    return {
      ...snapshot,
      authStatus: "authenticated",
      authReadiness: "ready",
      serverAuthAuthority: "authenticated",
      authorityRevision: 1,
      authInitialized: true,
      user,
      profile,
      accountContext: initialAccountContext || null,
      role: resolveRole(profile, user, initialRole),
      supportModeActive: Boolean(initialSupportModeActive),
    };
  }

  if (initialAuthResolved) {
    return {
      ...snapshot,
      authStatus: "unauthenticated",
      authReadiness: "ready",
      serverAuthAuthority: "confirmed_guest",
      authorityRevision: 1,
      authInitialized: true,
      supportModeActive: Boolean(initialSupportModeActive),
    };
  }

  return snapshot;
}

const authStore = {
  state: createEmptyAuthState(),
  listeners: new Set(),
  supabase: null,
  bootstrapPromise: null,
  profilePromise: null,
  profileUserId: null,
  businessPromise: null,
  businessUserId: null,
  providerCount: 0,
  guardSubscribed: false,
  authUnsubscribe: null,
  loggingOut: false,
  logoutRedirectInFlight: false,
  providerInstanceId: null,
  bootstrapAbortController: null,
  bootstrapRetryTimer: null,
  bootstrapWaitingForOnline: false,
  // A confirmed invalid refresh is terminal for the current browser runtime.
  // Only an explicit new SIGNED_IN event may open a new session generation.
  terminalSessionRejected: false,
  terminalCleanupPromise: null,
  sessionGeneration: 0,
  reconciliationAttemptId: 0,
  reconciliationCooldownUntil: 0,
  reconciliationLastResult: null,
  reconciliationPromise: null,
  resumeAttemptId: 0,
  resumePromise: null,
  resumeAbortController: null,
  resumeRetryTimer: null,
  resumeLifecycleCleanup: null,
  resumeWasBackgrounded: false,
  resumeBackgroundedAt: 0,
  resumeWasOffline: false,
  lastAuthenticatedAuthorityAt: 0,
  initialRootSeedApplied: false,
  initialRootSeedSnapshot: null,
  expiredSessionRedirectInFlight: false,
};

const AUTH_EVENT_NO_USER_VERIFY_EVENTS = new Set([
  "INITIAL_SESSION",
  "TOKEN_REFRESHED",
  "USER_UPDATED",
]);

let handledTokenInvalidAt = 0;
const authDiagEnabled =
  process.env.NEXT_PUBLIC_AUTH_DIAG === "1" &&
  process.env.NODE_ENV !== "production";
const autoRefreshDisabled =
  process.env.NEXT_PUBLIC_DISABLE_AUTO_REFRESH === "1" ||
  process.env.NEXT_PUBLIC_DISABLE_AUTO_REFRESH === "true";
export const AUTH_UI_RESET_EVENT = "yb-auth-ui-reset";
const AUTO_REFRESH_BLOCKED_EVENT = "yb-auto-refresh-blocked";
const AUTO_REFRESH_RETRY_EVENT = "yb-auto-refresh-retry";
const AUTO_REFRESH_WINDOW_MS = 10_000;
const AUTO_REFRESH_MAX_ATTEMPTS = 2;
const AUTO_REFRESH_COOLDOWN_MS = 30_000;
const AUTO_REFRESH_MIN_INTERVAL_MS = 2_500;
const PROFILE_RETRY_WINDOW_MS = 10_000;
const CLIENT_REDIRECT_STORAGE_KEYS = [
  "returnTo",
  "next",
  "callbackUrl",
  "postLoginRedirect",
  "yb:returnTo",
  "yb:postLoginRedirect",
  "business_auth_redirect",
  "business_auth_success",
  "yb_post_auth_redirect",
  "yb_auth_flow",
];
const CLIENT_REDIRECT_QUERY_KEYS = [
  "next",
  "returnUrl",
  "callbackUrl",
  "returnTo",
  "continue",
  "fromPath",
  "afterLogin",
  "postLogin",
];
const PROFILE_MAX_FAILURES = 2;
const PROFILE_COOLDOWN_MS = 30_000;

let authClickTracerRefs = 0;
let authClickTracerCleanup = null;
let autoRefreshAttemptTimestamps = [];
let autoRefreshBlockedUntil = 0;
let lastAutoRefreshAt = 0;
let profileFetchFailureTimestamps = [];
let profileFetchBlockedUntil = 0;
let profileFetchInFlight = null;
let profileFetchInFlightUserId = null;
const AUTO_REFRESH_GUARD_KEY = "yb_auto_refresh_guard";

function readRefreshGuardState() {
  if (typeof window === "undefined") {
    return {
      attempts: autoRefreshAttemptTimestamps,
      blockedUntil: autoRefreshBlockedUntil,
      lastRefreshAt: lastAutoRefreshAt,
    };
  }
  try {
    const raw = window.sessionStorage.getItem(AUTO_REFRESH_GUARD_KEY);
    if (!raw) {
      return {
        attempts: autoRefreshAttemptTimestamps,
        blockedUntil: autoRefreshBlockedUntil,
        lastRefreshAt: lastAutoRefreshAt,
      };
    }
    const parsed = JSON.parse(raw);
    return {
      attempts: Array.isArray(parsed?.attempts)
        ? parsed.attempts.filter((n) => typeof n === "number")
        : [],
      blockedUntil:
        typeof parsed?.blockedUntil === "number" ? parsed.blockedUntil : 0,
      lastRefreshAt:
        typeof parsed?.lastRefreshAt === "number" ? parsed.lastRefreshAt : 0,
    };
  } catch {
    return {
      attempts: autoRefreshAttemptTimestamps,
      blockedUntil: autoRefreshBlockedUntil,
      lastRefreshAt: lastAutoRefreshAt,
    };
  }
}

function writeRefreshGuardState(next) {
  const normalized = {
    attempts: Array.isArray(next?.attempts)
      ? next.attempts.filter((n) => typeof n === "number")
      : [],
    blockedUntil: typeof next?.blockedUntil === "number" ? next.blockedUntil : 0,
    lastRefreshAt: typeof next?.lastRefreshAt === "number" ? next.lastRefreshAt : 0,
  };
  autoRefreshAttemptTimestamps = normalized.attempts;
  autoRefreshBlockedUntil = normalized.blockedUntil;
  lastAutoRefreshAt = normalized.lastRefreshAt;
  if (typeof window === "undefined") return;
  try {
    if (
      normalized.attempts.length === 0 &&
      normalized.blockedUntil === 0 &&
      normalized.lastRefreshAt === 0
    ) {
      window.sessionStorage.removeItem(AUTO_REFRESH_GUARD_KEY);
      return;
    }
    window.sessionStorage.setItem(
      AUTO_REFRESH_GUARD_KEY,
      JSON.stringify(normalized)
    );
  } catch {
    // best effort
  }
}

function emitAuthState() {
  authStore.listeners.forEach((listener) => listener());
}

function setAuthState(nextState) {
  authStore.state = nextState;
  emitAuthState();
}

function updateAuthState(partial) {
  setAuthState({ ...authStore.state, ...partial });
}

function updateAuthAuthority(partial) {
  const authorityRevision = authStore.state.authorityRevision + 1;
  if (
    partial.authStatus === "authenticated" ||
    partial.serverAuthAuthority === "authenticated" ||
    partial.user?.id
  ) {
    authStore.expiredSessionRedirectInFlight = false;
  }
  setAuthState({
    ...authStore.state,
    ...partial,
    authorityRevision,
  });
  return authorityRevision;
}

const AUTH_DIAGNOSTIC_IDENTIFIER_KEYS = new Map([
  ["userId", "userPresent"],
  ["profileId", "profilePresent"],
  ["nextUserId", "nextUserPresent"],
  ["recoveredUserId", "recoveredUserPresent"],
  ["sessionUserId", "sessionUserPresent"],
  ["initialUserId", "initialUserPresent"],
  ["effectiveUserId", "effectiveUserPresent"],
]);

function sanitizeAuthDiagnosticPayload(payload = {}) {
  const sanitized = { ...payload };
  AUTH_DIAGNOSTIC_IDENTIFIER_KEYS.forEach((presenceKey, identifierKey) => {
    if (!Object.prototype.hasOwnProperty.call(sanitized, identifierKey)) return;
    sanitized[presenceKey] = Boolean(sanitized[identifierKey]);
    delete sanitized[identifierKey];
  });
  return sanitized;
}

function logAuthDiag(event, payload = {}) {
  if (!authDiagEnabled || typeof window === "undefined") return;
  console.log("[AUTH_DIAG]", {
    event,
    pathname: window.location.pathname,
    authStatus: authStore.state.authStatus,
    authBusy: authStore.state.authBusy,
    authAction: authStore.state.authAction,
    authAttemptId: authStore.state.authAttemptId,
    providerInstanceId: authStore.providerInstanceId,
    userPresent: Boolean(authStore.state.user?.id),
    ...sanitizeAuthDiagnosticPayload(payload),
  });
}

function logAuthConsistency(event, payload = {}) {
  if (
    process.env.NODE_ENV === "production" &&
    process.env.NEXT_PUBLIC_AUTH_DIAG !== "1"
  ) {
    return;
  }
  if (typeof window === "undefined") return;
  console.info("[AUTH_CONSISTENCY]", {
    event,
    tabId: getAuthTabId(),
    pathname: window.location.pathname,
    authStatus: authStore.state.authStatus,
    initialized: authStore.state.authInitialized,
    userPresent: Boolean(authStore.state.user?.id),
    profilePresent: Boolean(authStore.state.profile?.id),
    hasSession: Boolean(authStore.state.session),
    sessionGeneration: authStore.sessionGeneration,
    terminalSessionRejected: authStore.terminalSessionRejected,
    visibility: document.visibilityState,
    standalone:
      typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches,
    ...sanitizeAuthDiagnosticPayload(payload),
  });
}

function getAuthTabId() {
  if (typeof window === "undefined") return "server";
  try {
    const key = "yb_auth_tab_id";
    let id = window.sessionStorage.getItem(key);
    if (!id) {
      id = `tab-${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem(key, id);
    }
    return id;
  } catch {
    return "tab-unavailable";
  }
}

function emitAuthUiReset(reason) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(AUTH_UI_RESET_EVENT, {
      detail: { reason, ts: Date.now() },
    })
  );
  logAuthDiag("ui:reset", { reason });
}

function isAuthNavigationSuppressed() {
  return (
    authStore.loggingOut ||
    authStore.logoutRedirectInFlight ||
    isLogoutRedirectInFlight()
  );
}

function clearSupabaseCookiesClient() {
  if (typeof document === "undefined") return;
  const cookieName = getCookieName();
  const names = document.cookie
    .split(";")
    .map((entry) => entry.trim().split("=")[0])
    .filter(Boolean)
    .filter((name) => name.startsWith("sb-") || name === cookieName);

  const hostname = window.location.hostname || "";
  const domains = [undefined];
  if (hostname.endsWith("yourbarrio.com")) {
    domains.push(".yourbarrio.com", "www.yourbarrio.com");
  }

  names.forEach((name) => {
    domains.forEach((domain) => {
      const domainAttr = domain ? `domain=${domain};` : "";
      document.cookie = `${name}=; ${domainAttr} path=/; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
    });
  });

  try {
    localStorage.removeItem("business_auth_redirect");
    localStorage.removeItem("business_auth_success");
    localStorage.removeItem("signup_role");
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem("yb_auto_logged_out");
    sessionStorage.removeItem("auth_flow_id");
  } catch {
    // ignore
  }
  clearClientRedirectState();
}

async function rejectTerminalSession(reason) {
  if (authStore.terminalCleanupPromise) return authStore.terminalCleanupPromise;

  authStore.terminalSessionRejected = true;
  setExplicitLoginCorrelationRequired(true);
  authStore.sessionGeneration += 1;
  updateAuthAuthority({});
  logAuthConsistency("session:terminal_rejection", { reason });

  // Start this before deleting browser-visible cookies so the server can also
  // remove HttpOnly/domain cookies that the browser client cannot touch.
  const serverCleanup = fetchJsonWithTimeout("/api/auth/signout", {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    timeoutMs: AUTH_SESSION_STEP_TIMEOUT_MS,
  }).catch((error) => {
    logAuthConsistency("session:terminal_server_cleanup_failed", {
      reason,
      error: error?.name || "request_failed",
    });
  });

  authStore.terminalCleanupPromise = (async () => {
    clearSupabaseAuthStorage();
    clearSupabaseCookiesClient();
    try {
      await authStore.supabase?.auth?.signOut({ scope: "local" });
    } catch {
      // The local state and server cookie cleanup remain authoritative.
    }
    await cleanupRealtimeChannels();
    clearVerifiedUserCache();
    await serverCleanup;
    logAuthConsistency("session:terminal_cleanup_complete", { reason });
  })().finally(() => {
    authStore.terminalCleanupPromise = null;
  });

  return authStore.terminalCleanupPromise;
}

function normalizeClientRedirectStateInPlace() {
  if (typeof window === "undefined") return;
  const rewrites = [];

  const normalizeStorage = (storage, storageLabel) => {
    CLIENT_REDIRECT_STORAGE_KEYS.forEach((key) => {
      try {
        const current = storage.getItem(key);
        if (typeof current !== "string" || !current) return;
        const normalized = normalizeNextPath(current);
        if (!normalized || normalized === current) return;
        storage.setItem(key, normalized);
        rewrites.push({ from: current, to: normalized, source: `${storageLabel}.${key}` });
      } catch {
        // best effort
      }
    });
  };

  try {
    normalizeStorage(window.localStorage, "localStorage");
  } catch {
    // Accessing the Storage object itself can be denied in WebKit.
  }
  try {
    normalizeStorage(window.sessionStorage, "sessionStorage");
  } catch {
    // Accessing the Storage object itself can be denied in WebKit.
  }

  try {
    const url = new URL(window.location.href);
    let changed = false;
    CLIENT_REDIRECT_QUERY_KEYS.forEach((key) => {
      const current = url.searchParams.get(key);
      if (typeof current !== "string" || !current) return;
      const normalized = normalizeNextPath(current);
      if (!normalized || normalized === current) return;
      url.searchParams.set(key, normalized);
      rewrites.push({ from: current, to: normalized, source: `query.${key}` });
      changed = true;
    });
    if (changed) {
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
  } catch {
    // best effort
  }

  if (process.env.NODE_ENV !== "production") {
    rewrites.forEach(({ from, to, source }) => {
      console.info("[ONBOARDING_REDIRECT_TRACE] source=normalizeNextPath_rewrite", {
        from,
        to,
        storage: source,
      });
    });
  }
}

function describeNode(node) {
  if (!node || !node.tagName) return null;
  const id = node.id ? `#${node.id}` : "";
  const className =
    typeof node.className === "string" && node.className.trim()
      ? `.${node.className.trim().split(/\s+/).slice(0, 3).join(".")}`
      : "";
  return `${node.tagName.toLowerCase()}${id}${className}`;
}

function attachAuthClickTracer() {
  if (typeof window === "undefined") return () => {};
  const overlaySelectors = [
    "div[data-mobile-sidebar-drawer=\"1\"]",
    "#modal-root",
    "[aria-modal=\"true\"]",
  ];

  const handler = (event) => {
    const x = typeof event.clientX === "number" ? event.clientX : null;
    const y = typeof event.clientY === "number" ? event.clientY : null;
    const target = event.target;
    const path = typeof event.composedPath === "function" ? event.composedPath() : [];
    const pathSummary = path
      .slice(0, 6)
      .map((node) => describeNode(node))
      .filter(Boolean);
    const hit =
      x !== null && y !== null ? document.elementFromPoint(x, y) : null;
    const nav =
      document.querySelector("nav[data-nav-guard]") ||
      document.querySelector("nav[data-business-navbar]") ||
      document.querySelector("nav[data-nav-surface]") ||
      document.querySelector("nav[data-public-nav]") ||
      document.querySelector("nav");
    const overlayNodes = overlaySelectors
      .map((selector) => ({ selector, node: document.querySelector(selector) }))
      .filter(({ node }) => node);
    const overlayHit = overlayNodes.find(({ node }) => hit && node.contains(hit));

    console.log("[AUTH_DIAG] click:capture", {
      type: event.type,
      coords: { x, y },
      target: describeNode(target),
      path: pathSummary,
      elementFromPoint: describeNode(hit),
      inNavbar: Boolean(nav && hit && nav.contains(hit)),
      overlayHit: overlayHit
        ? { selector: overlayHit.selector, node: describeNode(overlayHit.node) }
        : null,
    });
  };

  window.addEventListener("pointerdown", handler, true);
  window.addEventListener("click", handler, true);
  return () => {
    window.removeEventListener("pointerdown", handler, true);
    window.removeEventListener("click", handler, true);
  };
}

function buildAuthUiResetState(reason) {
  const nextAttemptId = authStore.state.authAttemptId + 1;
  logAuthDiag("auth_ui:reset", { reason, nextAttemptId });
  return {
    authBusy: false,
    authAction: null,
    authAttemptId: nextAttemptId,
    authActionStartedAt: 0,
  };
}

function applySignedOutState(reason = "signed_out", options = {}) {
  const {
    resetGuardState = false,
    clearAuthSuppression = false,
    extraState = null,
    resetAuthUi = true,
  } = options;

  if (resetGuardState) {
    writeRefreshGuardState({
      attempts: [],
      blockedUntil: 0,
      lastRefreshAt: 0,
    });
  }

  if (authStore.bootstrapAbortController?.abort) {
    authStore.bootstrapAbortController.abort();
  }
  authStore.bootstrapAbortController = null;
  authStore.bootstrapPromise = null;
  if (authStore.bootstrapRetryTimer) {
    window.clearTimeout(authStore.bootstrapRetryTimer);
    authStore.bootstrapRetryTimer = null;
  }
  authStore.bootstrapWaitingForOnline = false;
  authStore.profilePromise = null;
  authStore.profileUserId = null;
  authStore.businessPromise = null;
  authStore.businessUserId = null;
  authStore.resumeAttemptId += 1;
  authStore.resumeAbortController?.abort?.();
  authStore.resumeAbortController = null;
  authStore.resumePromise = null;
  if (authStore.resumeRetryTimer) {
    window.clearTimeout(authStore.resumeRetryTimer);
    authStore.resumeRetryTimer = null;
  }
  authStore.resumeWasBackgrounded = false;
  authStore.resumeBackgroundedAt = 0;
  authStore.resumeWasOffline = false;
  authStore.lastAuthenticatedAuthorityAt = 0;

  if (clearAuthSuppression) {
    authStore.loggingOut = false;
  }

  const signedOutBase = resetGuardState
    ? {
        ...buildSignedOutState(),
        rateLimited: false,
        rateLimitUntil: 0,
        rateLimitMessage: null,
        tokenInvalidAt: 0,
      }
    : withGuardState(buildSignedOutState());

  const authUiState = resetAuthUi
    ? buildAuthUiResetState(`signed_out:${reason}`)
    : null;

  updateAuthAuthority({
    ...signedOutBase,
    ...(authUiState || {}),
    ...(extraState || {}),
  });

  logAuthDiag("auth:signed_out:applied", { reason, resetGuardState });
}

function beginAuthAttempt(action) {
  const nextAttemptId = authStore.state.authAttemptId + 1;
  updateAuthState({
    authBusy: true,
    authAction: action || null,
    authAttemptId: nextAttemptId,
    authActionStartedAt: Date.now(),
    lastError: null,
  });
  logAuthDiag("auth:attempt:begin", { action, attemptId: nextAttemptId });
  return nextAttemptId;
}

function endAuthAttempt(attemptId, result) {
  if (attemptId !== authStore.state.authAttemptId) {
    logAuthDiag("auth:attempt:end:ignored", {
      attemptId,
      currentAttemptId: authStore.state.authAttemptId,
      result,
    });
    return false;
  }

  updateAuthState({
    authBusy: false,
    authAction: null,
    authActionStartedAt: 0,
  });
  logAuthDiag("auth:attempt:end", { attemptId, result });
  return true;
}

function resetAuthUiState(reason) {
  updateAuthState(buildAuthUiResetState(reason));
}

async function cleanupRealtimeChannels() {
  await stopRealtime(authStore.supabase);
}

function setAuthError(error) {
  if (!error) return;
  const message = error?.message || String(error);
  updateAuthState({
    error,
    lastError: message,
  });
}

function applyRecoverableAuthState(
  error,
  reason = "auth_recovery",
  { terminal = true } = {}
) {
  const message =
    error?.message ||
    "Your session could not be checked. Check your connection and try again.";
  updateAuthState({
    authStatus: authStore.state.user?.id ? "authenticated" : "loading",
    authReadiness: terminal ? "recoverable_error" : "initializing",
    authInitialized: true,
    error: error || new Error(message),
    lastError: message,
  });
  logAuthConsistency(
    terminal ? "recovery:terminal_failure" : "recovery:retry_pending",
    { reasonCode: reason }
  );
}

function subscribeAuthState(listener) {
  authStore.listeners.add(listener);
  return () => {
    authStore.listeners.delete(listener);
  };
}

function getAuthStateSnapshot() {
  return authStore.state;
}

function primeAuthStateForInitialRender({
  initialUser,
  initialProfile,
  initialBusiness = null,
  initialAccountContext = null,
  initialRole,
  initialAuthResolved = false,
  supportModeActive = false,
  providerSeedToken = null,
}) {
  const previousSeed = authStore.initialRootSeedSnapshot;
  if (
    previousSeed &&
    previousSeed.user === initialUser &&
    previousSeed.profile === initialProfile &&
    previousSeed.business === initialBusiness &&
    previousSeed.accountContext === initialAccountContext &&
    previousSeed.role === initialRole &&
    previousSeed.authResolved === initialAuthResolved &&
    previousSeed.supportModeActive === supportModeActive &&
    previousSeed.providerSeedToken === providerSeedToken
  ) {
    return;
  }
  authStore.initialRootSeedSnapshot = {
    user: initialUser,
    profile: initialProfile,
    business: initialBusiness,
    accountContext: initialAccountContext,
    role: initialRole,
    authResolved: initialAuthResolved,
    supportModeActive,
    providerSeedToken,
  };
  if (supportModeActive) return;
  if (authStore.loggingOut || authStore.logoutRedirectInFlight) return;
  if (authStore.terminalSessionRejected) {
    logAuthConsistency("server_seed:ignored_terminal_rejection");
    return;
  }
  if (!initialUser?.id) {
    if (!initialAuthResolved) return;
    if (authStore.initialRootSeedApplied) return;
    authStore.initialRootSeedApplied = true;
    if (
      authStore.state.authStatus === "authenticated" ||
      authStore.state.user?.id ||
      authStore.state.serverAuthAuthority === "authenticated"
    ) {
      logAuthConsistency("server_seed:ignored_lower_authority_guest");
      return;
    }
    if (
      authStore.state.authStatus !== "unauthenticated" ||
      authStore.state.authReadiness !== "ready" ||
      authStore.state.serverAuthAuthority !== "confirmed_guest" ||
      !authStore.state.authInitialized ||
      authStore.state.user ||
      authStore.state.profile ||
      authStore.state.business ||
      authStore.state.accountContext ||
      authStore.state.role
    ) {
      authStore.state = {
        ...authStore.state,
        authStatus: "unauthenticated",
        authReadiness: "ready",
        authReconciliationStatus: "idle",
        serverAuthAuthority: "confirmed_guest",
        authorityRevision: authStore.state.authorityRevision + 1,
        authInitialized: true,
        session: null,
        user: null,
        profile: null,
        business: null,
        accountContext: null,
        role: null,
        error: null,
      };
    }
    return;
  }
  authStore.initialRootSeedApplied = true;
  const currentUserId = authStore.state.user?.id || null;
  if (
    authStore.sessionGeneration > 0 &&
    currentUserId &&
    currentUserId !== initialUser.id
  ) {
    // A server component snapshot can be older than a just-completed client
    // login. Never let it replace a newer browser-authenticated identity.
    logAuthConsistency("server_seed:ignored_superseded_identity");
    return;
  }
  const nextUser = mergeAuthUser(authStore.state.user, initialUser);
  const nextProfile = initialProfile
    ? mergeFreshProfile(keepProfileForUser(authStore.state.profile, nextUser), initialProfile, nextUser)
    : keepProfileForUser(authStore.state.profile, nextUser);
  const nextBusiness =
    initialBusiness ||
    (currentUserId === initialUser.id ? authStore.state.business : null);
  const fallbackRole = currentUserId === initialUser.id ? authStore.state.role : null;
  authStore.expiredSessionRedirectInFlight = false;
  authStore.lastAuthenticatedAuthorityAt = Date.now();
  authStore.state = {
    ...authStore.state,
    authStatus: "authenticated",
    authReadiness: "ready",
    authReconciliationStatus: "idle",
    serverAuthAuthority: "authenticated",
    authResumeStatus: "idle",
    authResumeError: null,
    businessContextStatus: nextBusiness ? "authorized" : "idle",
    authorityRevision: authStore.state.authorityRevision + 1,
    authInitialized: true,
    user: nextUser,
    profile: nextProfile,
    business: nextBusiness,
    accountContext: initialAccountContext || authStore.state.accountContext || {
      hasBusinessAccess: Boolean(nextBusiness),
      businessRowExists: Boolean(nextBusiness),
      businessAccessContexts: [],
    },
    role: resolveRole(nextProfile, nextUser, initialRole || fallbackRole),
    error: null,
  };
  if (nextUser?.id && nextProfile?.id === nextUser.id) {
    authStore.profileUserId = nextUser.id;
  }
  if (nextUser?.id && nextBusiness?.owner_user_id === nextUser.id) {
    authStore.businessUserId = nextUser.id;
  }
}

export function __resetAuthStoreForTests() {
  if (process.env.NODE_ENV === "production") return;
  authStore.state = createEmptyAuthState();
  authStore.listeners.clear();
  authStore.supabase = null;
  authStore.bootstrapPromise = null;
  authStore.profilePromise = null;
  authStore.profileUserId = null;
  authStore.businessPromise = null;
  authStore.businessUserId = null;
  authStore.providerCount = 0;
  authStore.guardSubscribed = false;
  if (authStore.authUnsubscribe) {
    authStore.authUnsubscribe();
  }
  authStore.authUnsubscribe = null;
  authStore.loggingOut = false;
  authStore.logoutRedirectInFlight = false;
  authStore.providerInstanceId = null;
  authStore.bootstrapAbortController = null;
  if (authStore.bootstrapRetryTimer) {
    window.clearTimeout(authStore.bootstrapRetryTimer);
    authStore.bootstrapRetryTimer = null;
  }
  authStore.bootstrapWaitingForOnline = false;
  authStore.terminalSessionRejected = false;
  setExplicitLoginCorrelationRequired(false);
  authStore.terminalCleanupPromise = null;
  authStore.sessionGeneration = 0;
  authStore.reconciliationAttemptId = 0;
  authStore.reconciliationCooldownUntil = 0;
  authStore.reconciliationLastResult = null;
  authStore.reconciliationPromise = null;
  authStore.resumeAttemptId = 0;
  authStore.resumePromise = null;
  authStore.resumeAbortController?.abort?.();
  authStore.resumeAbortController = null;
  if (authStore.resumeRetryTimer) {
    window.clearTimeout(authStore.resumeRetryTimer);
    authStore.resumeRetryTimer = null;
  }
  authStore.resumeLifecycleCleanup?.();
  authStore.resumeLifecycleCleanup = null;
  authStore.resumeWasBackgrounded = false;
  authStore.resumeBackgroundedAt = 0;
  authStore.resumeWasOffline = false;
  authStore.lastAuthenticatedAuthorityAt = 0;
  authStore.initialRootSeedApplied = false;
  authStore.initialRootSeedSnapshot = null;
  authStore.expiredSessionRedirectInFlight = false;
  handledTokenInvalidAt = 0;
  profileFetchInFlight = null;
  profileFetchInFlightUserId = null;
  profileFetchBlockedUntil = 0;
  profileFetchFailureTimestamps = [];
}

function seedAuthState({
  initialUser,
  initialProfile,
  initialBusiness = null,
  initialAccountContext = null,
  initialRole,
  supportModeActive = false,
}) {
  if (
    !initialUser &&
    !initialProfile &&
    !initialBusiness &&
    !initialAccountContext &&
    !initialRole &&
    !supportModeActive
  ) {
    return;
  }

  const nextState = { ...authStore.state };
  let changed = false;
  let authorityPublished = false;

  if (supportModeActive) {
    const nextUser = initialUser ?? null;
    const nextProfile = initialProfile ?? null;
    const nextBusiness = initialBusiness ?? null;
    const nextRole = resolveRole(nextProfile, nextUser, initialRole ?? "customer");
    if (
      nextState.user?.id !== nextUser?.id ||
      nextState.profile?.id !== nextProfile?.id ||
      nextState.business?.owner_user_id !== nextBusiness?.owner_user_id ||
      nextState.business?.business_name !== nextBusiness?.business_name ||
      nextState.role !== nextRole ||
      nextState.authStatus !== (nextUser ? "authenticated" : "unauthenticated") ||
      nextState.supportModeActive !== true
    ) {
      nextState.user = nextUser;
      nextState.profile = nextProfile;
      nextState.business = nextBusiness;
      nextState.role = nextRole;
      nextState.authStatus = nextUser ? "authenticated" : "unauthenticated";
      nextState.authReadiness = "ready";
      nextState.authReconciliationStatus = "idle";
      nextState.serverAuthAuthority = nextUser ? "authenticated" : "confirmed_guest";
      nextState.authResumeStatus = "idle";
      nextState.authResumeError = null;
      nextState.businessContextStatus = nextBusiness ? "authorized" : "idle";
      nextState.supportModeActive = true;
      changed = true;
      authorityPublished = true;
      if (authDiagEnabled) {
        console.warn("[auth] init", {
          supportModeActive: true,
          effectiveUserId: nextUser?.id ?? null,
          effectiveEmail: nextUser?.email ?? null,
        });
      }
    }
  } else if (nextState.supportModeActive) {
    nextState.supportModeActive = false;
    changed = true;
  }

  if (
    !supportModeActive &&
    initialUser &&
    (!nextState.user || nextState.user.id === initialUser.id)
  ) {
    nextState.user = mergeAuthUser(nextState.user, initialUser);
    nextState.authStatus = "authenticated";
    nextState.authReadiness = "ready";
    nextState.authReconciliationStatus = "idle";
    nextState.serverAuthAuthority = "authenticated";
    nextState.authResumeError = null;
    changed = true;
    authorityPublished = true;
  }

  if (!supportModeActive && initialProfile && !nextState.profile) {
    nextState.profile = mergeFreshProfile(nextState.profile, initialProfile, nextState.user);
    changed = true;
  } else if (
    !supportModeActive &&
    initialProfile &&
    nextState.profile?.id === initialProfile.id
  ) {
    const mergedProfile = mergeFreshProfile(nextState.profile, initialProfile, nextState.user);
    if (!areProfilesEqual(mergedProfile, nextState.profile)) {
      nextState.profile = mergedProfile;
      changed = true;
    }
  }

  if (!supportModeActive && initialBusiness) {
    const seedUserMatches =
      !initialUser?.id || nextState.user?.id === initialUser.id;
    const seedMatchesCurrentBusiness =
      !nextState.business ||
      (Boolean(nextState.business?.id) &&
        nextState.business.id === initialBusiness.id);
    if (seedUserMatches && seedMatchesCurrentBusiness) {
      const mergedBusiness = mergeBusinessAccessState(
        nextState.business,
        initialBusiness,
        nextState.user
      );
      if (JSON.stringify(mergedBusiness) !== JSON.stringify(nextState.business)) {
        nextState.business = mergedBusiness;
        changed = true;
      }
      nextState.businessContextStatus = "authorized";
    }
  }

  if (!supportModeActive && initialAccountContext) {
    nextState.accountContext = initialAccountContext;
    changed = true;
  }

  if (!supportModeActive && initialRole && !nextState.role) {
    nextState.role = initialRole;
    changed = true;
  }

  if (changed) {
    nextState.role = resolveRole(nextState.profile, nextState.user, nextState.role);
    if (authorityPublished) {
      nextState.authorityRevision = authStore.state.authorityRevision + 1;
      if (nextState.serverAuthAuthority === "authenticated" && nextState.user?.id) {
        authStore.expiredSessionRedirectInFlight = false;
      }
    }
    setAuthState(nextState);
  }
  if (nextState.user?.id && nextState.profile?.id === nextState.user.id) {
    authStore.profileUserId = nextState.user.id;
  }
  if (nextState.user?.id && nextState.business?.owner_user_id === nextState.user.id) {
    authStore.businessUserId = nextState.user.id;
  }
}

function syncAuthGuardState(guard) {
  const rateLimited = guard.cooldownMsRemaining > 0;
  const rateLimitUntil = guard.cooldownUntil || 0;
  const rateLimitMessage = rateLimited
    ? "We're having trouble connecting. Please wait a moment."
    : null;

  const next = { ...authStore.state };
  let changed = false;

  if (next.rateLimited !== rateLimited) {
    next.rateLimited = rateLimited;
    changed = true;
  }

  if (next.rateLimitUntil !== rateLimitUntil) {
    next.rateLimitUntil = rateLimitUntil;
    changed = true;
  }

  if (next.rateLimitMessage !== rateLimitMessage) {
    next.rateLimitMessage = rateLimitMessage;
    changed = true;
  }

  if (guard.tokenInvalidAt && next.tokenInvalidAt !== guard.tokenInvalidAt) {
    next.tokenInvalidAt = guard.tokenInvalidAt;
    changed = true;
  }

  if (typeof guard.refreshDisabledUntil === "number") {
    if (next.refreshDisabledUntil !== guard.refreshDisabledUntil) {
      next.refreshDisabledUntil = guard.refreshDisabledUntil;
      changed = true;
    }
  }

  if (next.refreshDisabledReason !== (guard.refreshDisabledReason || null)) {
    next.refreshDisabledReason = guard.refreshDisabledReason || null;
    changed = true;
  }

  if (changed) {
    setAuthState(next);
  }
}

function ensureAuthGuardSubscription() {
  if (authStore.guardSubscribed) return;
  authStore.guardSubscribed = true;
  syncAuthGuardState(getAuthGuardState());
  subscribeAuthGuard((guard) => {
    syncAuthGuardState(guard);
  });
}

async function fetchProfile(user) {
  if (!user?.id) {
    return { profile: null, accountContext: null, error: null };
  }
  const now = Date.now();
  if (profileFetchBlockedUntil > now) {
    return {
      profile: null,
      accountContext: null,
      error: {
        message: "Profile refresh temporarily paused due to repeated failures.",
        code: "profile_fetch_cooldown",
      },
    };
  }

  if (profileFetchInFlight && profileFetchInFlightUserId === user.id) {
    return profileFetchInFlight;
  }

  profileFetchInFlightUserId = user.id;
  profileFetchInFlight = (async () => {
    try {
      const res = await fetch("/api/me", {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401) {
          return { profile: null, accountContext: null, error: null };
        }
        if (res.status !== 401) {
          const nowFail = Date.now();
          profileFetchFailureTimestamps = profileFetchFailureTimestamps.filter(
            (ts) => nowFail - ts < PROFILE_RETRY_WINDOW_MS
          );
          profileFetchFailureTimestamps.push(nowFail);
          if (profileFetchFailureTimestamps.length >= PROFILE_MAX_FAILURES) {
            profileFetchBlockedUntil = nowFail + PROFILE_COOLDOWN_MS;
          }
        }
        return {
          profile: null,
          accountContext: null,
          error: { message: payload?.error || "Failed to load profile" },
        };
      }
      profileFetchFailureTimestamps = [];
      profileFetchBlockedUntil = 0;
      return {
        profile: payload?.profile ?? null,
        accountContext: payload?.accountContext ?? null,
        error: null,
      };
    } catch (err) {
      const nowFail = Date.now();
      profileFetchFailureTimestamps = profileFetchFailureTimestamps.filter(
        (ts) => nowFail - ts < PROFILE_RETRY_WINDOW_MS
      );
      profileFetchFailureTimestamps.push(nowFail);
      if (profileFetchFailureTimestamps.length >= PROFILE_MAX_FAILURES) {
        profileFetchBlockedUntil = nowFail + PROFILE_COOLDOWN_MS;
      }
      return { profile: null, accountContext: null, error: err };
    } finally {
      profileFetchInFlight = null;
      profileFetchInFlightUserId = null;
    }
  })();
  return profileFetchInFlight;
}

async function fetchBusiness(user) {
  if (!user?.id) {
    return { business: null, error: null };
  }
  if (!authStore.supabase) {
    return { business: null, error: null };
  }
  try {
    const { data, error } = await authStore.supabase
      .from("businesses")
      .select("id,business_name,owner_user_id,marketplace_activation_status")
      .eq("owner_user_id", user.id)
      .maybeSingle();
    if (error) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[AUTH_GUARD_DIAG] auth_provider:business_fetch_failed", {
          userId: user.id,
          message: error.message || null,
          code: error.code || null,
        });
      }
      return { business: null, error };
    }
    return { business: data ?? null, error: null };
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[AUTH_GUARD_DIAG] auth_provider:business_fetch_error", {
        userId: user.id,
        message: error?.message || String(error),
      });
    }
    return { business: null, error };
  }
}

function mergeBusinessAccessState(prevBusiness, nextBusiness, user) {
  if (!nextBusiness) return null;
  const isSameBusiness =
    prevBusiness?.id === nextBusiness.id ||
    (prevBusiness?.owner_user_id &&
      prevBusiness.owner_user_id === nextBusiness.owner_user_id);
  const merged = isSameBusiness
    ? {
        ...prevBusiness,
        ...nextBusiness,
      }
    : { ...nextBusiness };
  const isOwner =
    merged.isOwner === true ||
    (Boolean(user?.id) && merged.owner_user_id === user.id);

  if (!isOwner) return merged;

  return {
    ...merged,
    isOwner: true,
    role: merged.role || "owner",
    businessAccessRole: merged.businessAccessRole || "owner",
    canAccessDashboard: merged.canAccessDashboard !== false,
    canManageStaff: merged.canManageStaff !== false,
    canManagePayouts: merged.canManagePayouts !== false,
    canManageListings: merged.canManageListings !== false,
    canManageOrders: merged.canManageOrders !== false,
    canAccessMessages: merged.canAccessMessages !== false,
    canManageInventory: merged.canManageInventory !== false,
    canManageProfile: merged.canManageProfile !== false,
    canManageOwnStaffProfile: merged.canManageOwnStaffProfile !== false,
  };
}

function mergeBusinessEnrichmentState(
  prevBusiness,
  nextBusiness,
  user,
  businessContextStatus
) {
  const hasAuthoritativeBusiness =
    businessContextStatus === "authorized" && Boolean(prevBusiness?.id);

  if (hasAuthoritativeBusiness) {
    // The browser enrichment query only resolves owner-owned rows. It cannot
    // authoritatively revoke a server-seeded staff membership or switch the
    // active business, and a late response must not erase permission fields.
    if (!nextBusiness?.id || nextBusiness.id !== prevBusiness.id) {
      return prevBusiness;
    }
  }

  return mergeBusinessAccessState(prevBusiness, nextBusiness, user);
}

async function getProfileForUser(user) {
  if (!user?.id) return { profile: null, error: null };
  if (authStore.profilePromise && authStore.profileUserId === user.id) {
    return authStore.profilePromise;
  }
  if (authStore.profileUserId === user.id && authStore.state.profile) {
    return { profile: authStore.state.profile, error: null };
  }

  authStore.profileUserId = user.id;
  authStore.profilePromise = fetchProfile(user)
    .finally(() => {
      authStore.profilePromise = null;
    });
  return authStore.profilePromise;
}

function keepProfileForUser(profile, user) {
  if (!profile || !user?.id) return null;
  if (!profile.id || profile.id === user.id) return profile;
  return null;
}

async function getBusinessForUser(user) {
  if (!user?.id) return { business: null, error: null };
  if (authStore.businessPromise && authStore.businessUserId === user.id) {
    return authStore.businessPromise;
  }
  if (authStore.businessUserId === user.id && authStore.state.business) {
    return { business: authStore.state.business, error: null };
  }

  authStore.businessUserId = user.id;
  authStore.businessPromise = fetchBusiness(user).finally(() => {
    authStore.businessPromise = null;
  });
  return authStore.businessPromise;
}

async function fetchServerAccountSnapshot(reason = "bootstrap", signal) {
  try {
    logAuthConsistency("server_snapshot:start", { reason });
    const { response: res, payload } = await fetchJsonWithTimeout("/api/me", {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      signal,
      timeoutMs: AUTH_SERVER_SNAPSHOT_TIMEOUT_MS,
      headers: {
        "x-yb-auth-bootstrap": reason,
      },
    });
    const user = payload?.user ?? null;
    if (res.status === 401) {
      logAuthConsistency("server_snapshot:guest", {
        reason,
        status: res.status,
      });
      return {
        user: null,
        profile: null,
        accountContext: null,
        role: null,
        error: null,
        confirmedGuest: true,
      };
    }
    if (!res.ok || !user?.id) {
      logAuthConsistency("server_snapshot:empty", {
        reason,
        status: res.status,
        hasUser: Boolean(user?.id),
      });
      const error = new Error(
        res.ok
          ? "The server returned an incomplete session."
          : payload?.error || `Session check failed with status ${res.status}.`
      );
      error.status = res.status;
      error.kind = res.status >= 500 ? "server" : "network";
      return { user: null, profile: null, accountContext: null, role: null, error, confirmedGuest: false };
    }
    const profile = payload?.profile ?? null;
    const role =
      payload?.accountContext?.role ||
      profile?.role ||
      user?.app_metadata?.role ||
      null;
    logAuthConsistency("server_snapshot:resolved", {
      reason,
      status: res.status,
      userId: user.id,
      hasProfile: Boolean(profile?.id),
      role,
    });
    return {
      user,
      profile,
      accountContext: payload?.accountContext ?? null,
      role,
      error: null,
      confirmedGuest: false,
    };
  } catch (error) {
    logAuthConsistency("server_snapshot:error", {
      reason,
      error: error?.message || String(error),
    });
    return { user: null, profile: null, accountContext: null, role: null, error, confirmedGuest: false };
  }
}

function isAuthHandoffUrl() {
  if (typeof window === "undefined") return false;
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(AUTH_HANDOFF_PARAM) === "1";
  } catch {
    return false;
  }
}

async function waitForAuthRetry(delayMs, signal) {
  if (!delayMs) return;
  await new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason || new Error("Auth bootstrap aborted"));
      return;
    }
    const handleAbort = () => {
      window.clearTimeout(timer);
      reject(signal.reason || new Error("Auth bootstrap aborted"));
    };
    const timer = window.setTimeout(() => {
      signal?.removeEventListener?.("abort", handleAbort);
      resolve();
    }, delayMs);
    signal?.addEventListener?.("abort", handleAbort, { once: true });
  });
}

async function fetchServerAccountSnapshotWithHandoffRetry(reason = "bootstrap", signal) {
  const authHandoff = isAuthHandoffUrl();
  const maxAttempts = authHandoff ? 5 : 1;
  let lastSnapshot = { user: null, profile: null, accountContext: null, role: null, error: null, confirmedGuest: false };
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    lastSnapshot = await fetchServerAccountSnapshot(
      attempt === 1 ? reason : `${reason}_retry_${attempt}`,
      signal
    );
    const hasUsableHandoffSnapshot =
      Boolean(lastSnapshot.user?.id) &&
      (!isAuthHandoffUrl() || Boolean(lastSnapshot.profile?.id));
    if (hasUsableHandoffSnapshot) {
      if (attempt > 1) {
        logAuthConsistency("server_snapshot:handoff_retry_recovered", {
          reason,
          attempt,
          userId: lastSnapshot.user.id,
          hasProfile: Boolean(lastSnapshot.profile?.id),
          role: lastSnapshot.role || null,
        });
      }
      return lastSnapshot;
    }
    if ((lastSnapshot.confirmedGuest && (!authHandoff || attempt === maxAttempts)) || signal?.aborted) {
      return lastSnapshot;
    }
    if (attempt < maxAttempts) {
      logAuthConsistency("server_snapshot:handoff_retry_wait", {
        reason,
        attempt,
      });
      try {
        await waitForAuthRetry(200, signal);
      } catch (error) {
        return { ...lastSnapshot, error: lastSnapshot.error || error };
      }
    }
  }
  return lastSnapshot;
}

function getAuthReconciliationFailureCategory(snapshot) {
  const error = snapshot?.error;
  const status = Number(error?.status);
  const name = String(error?.name || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();
  if (name === "timeouterror" || name === "aborterror" || message.includes("timed out")) {
    return "timeout";
  }
  if (status === 429) return "rate_limited";
  if (status >= 500) return "server_error";
  if (error?.kind === "network" || name === "typeerror") return "transport";
  return error ? "incomplete_or_transport" : "incomplete";
}

function logAuthReconciliation({
  attemptId,
  trigger,
  result,
  failureCategory = null,
  redirectSuppressed = false,
  restoredAuthenticated = false,
  terminalCleanupProceeded = false,
  generation,
  authorityRevision,
}) {
  if (typeof window === "undefined") return;
  if (process.env.NEXT_PUBLIC_AUTH_DIAG !== "1") return;
  console.info("[AUTH_RECONCILIATION]", {
    attemptId,
    trigger,
    result,
    failureCategory,
    redirectSuppressed,
    restoredAuthenticated,
    terminalCleanupProceeded,
    generation,
    authorityRevision,
  });
}

function restoreAuthenticatedServerSnapshot(serverSnapshot, lastAuthEvent) {
  const sameUser = authStore.state.user?.id === serverSnapshot.user?.id;
  const nextUser = mergeAuthUser(authStore.state.user, serverSnapshot.user);
  const nextProfile = serverSnapshot.profile
    ? mergeFreshProfile(
        keepProfileForUser(authStore.state.profile, nextUser),
        serverSnapshot.profile,
        nextUser
      )
    : keepProfileForUser(authStore.state.profile, nextUser);
  updateAuthAuthority({
    ...withGuardState(
      buildSignedInState({
        user: nextUser,
        profile: nextProfile,
        business: sameUser ? authStore.state.business : null,
        accountContext:
          serverSnapshot.accountContext ??
          (sameUser ? authStore.state.accountContext : null),
      })
    ),
    role: resolveRole(nextProfile, nextUser, serverSnapshot.role),
    session: sameUser ? authStore.state.session : null,
    authInitialized: true,
    authReadiness: "ready",
    authReconciliationStatus: "idle",
    serverAuthAuthority: "authenticated",
    lastAuthEvent,
    error: null,
    lastError: null,
  });
  authStore.lastAuthenticatedAuthorityAt = Date.now();
}

async function reconcileClientAuthWithServer(
  trigger,
  { signal, bypassUncertainCooldown = false } = {}
) {
  if (
    authStore.loggingOut ||
    authStore.logoutRedirectInFlight ||
    authStore.terminalSessionRejected
  ) {
    return { result: "aborted", failureCategory: "explicit_or_terminal_flow" };
  }
  if (authStore.reconciliationPromise) {
    return authStore.reconciliationPromise;
  }
  if (
    !bypassUncertainCooldown &&
    authStore.reconciliationLastResult?.result === "uncertain" &&
    Date.now() < authStore.reconciliationCooldownUntil
  ) {
    return authStore.reconciliationLastResult;
  }

  const attemptId = authStore.reconciliationAttemptId + 1;
  const generation = authStore.sessionGeneration;
  const authorityRevision = authStore.state.authorityRevision;
  authStore.reconciliationAttemptId = attemptId;
  updateAuthState({
    authReconciliationStatus: "in_progress",
    serverAuthAuthority: "reconciling",
    ...(authStore.state.user?.id ? {} : { authReadiness: "initializing" }),
  });

  let sharedPromise;
  sharedPromise = (async () => {
    const serverSnapshot = await fetchServerAccountSnapshotWithHandoffRetry(
      `auth_reconciliation_${trigger}`,
      signal
    );
    if (
      authStore.loggingOut ||
      authStore.logoutRedirectInFlight ||
      authStore.sessionGeneration !== generation ||
      authStore.reconciliationAttemptId !== attemptId ||
      authStore.state.authorityRevision !== authorityRevision
    ) {
      const aborted = {
        result: "aborted",
        failureCategory: "generation_changed",
        attemptId,
        generation,
        authorityRevision,
      };
      logAuthReconciliation({
        ...aborted,
        trigger,
        redirectSuppressed: true,
      });
      if (
        !authStore.loggingOut &&
        !authStore.logoutRedirectInFlight &&
        authStore.state.user?.id &&
        authStore.state.authStatus === "authenticated"
      ) {
        updateAuthState({
          authReconciliationStatus: "idle",
          serverAuthAuthority: "authenticated",
        });
      }
      return aborted;
    }

    if (serverSnapshot.user?.id) {
      restoreAuthenticatedServerSnapshot(
        serverSnapshot,
        `${String(trigger || "auth").toUpperCase()}_RECONCILED_AUTHENTICATED`
      );
      const authenticated = {
        result: "authenticated",
        failureCategory: null,
        attemptId,
        generation,
        authorityRevision: authStore.state.authorityRevision,
        userId: authStore.state.user?.id || null,
      };
      authStore.reconciliationCooldownUntil = 0;
      authStore.reconciliationLastResult = authenticated;
      if (isPwaDeploymentRecoveryActive()) {
        recordPwaSessionExpirySuppressed();
      }
      logAuthReconciliation({
        ...authenticated,
        trigger,
        redirectSuppressed: true,
        restoredAuthenticated: true,
      });
      return authenticated;
    }

    if (serverSnapshot.confirmedGuest) {
      const confirmedGuestRevision = updateAuthAuthority({
        authReconciliationStatus: "idle",
        serverAuthAuthority: "confirmed_guest",
        authReadiness: "ready",
      });
      const confirmedGuest = {
        result: "confirmed_guest",
        failureCategory: null,
        attemptId,
        generation,
        authorityRevision: confirmedGuestRevision,
      };
      authStore.reconciliationCooldownUntil = 0;
      authStore.reconciliationLastResult = confirmedGuest;
      logAuthReconciliation({
        ...confirmedGuest,
        trigger,
        terminalCleanupProceeded: false,
      });
      return confirmedGuest;
    }

    const failureCategory = getAuthReconciliationFailureCategory(serverSnapshot);
    const uncertain = {
      result: "uncertain",
      failureCategory,
      attemptId,
      generation,
      authorityRevision,
    };
    authStore.reconciliationCooldownUntil =
      Date.now() + AUTH_RECONCILIATION_UNCERTAIN_COOLDOWN_MS;
    authStore.reconciliationLastResult = uncertain;
    applyRecoverableAuthState(
      serverSnapshot.error ||
        new Error("Session confirmation is temporarily unavailable."),
      `auth_reconciliation_${trigger}_uncertain`
    );
    updateAuthState({
      authReconciliationStatus: "uncertain",
      serverAuthAuthority: "uncertain",
    });
    if (isPwaDeploymentRecoveryActive()) {
      recordPwaSessionExpirySuppressed();
    }
    logAuthReconciliation({
      ...uncertain,
      trigger,
      redirectSuppressed: true,
    });
    return uncertain;
  })().finally(() => {
    if (authStore.reconciliationPromise === sharedPromise) {
      authStore.reconciliationPromise = null;
    }
  });
  authStore.reconciliationPromise = sharedPromise;
  return sharedPromise;
}

function createAuthResumeError(message, { code = "auth_resume_failed", status = null } = {}) {
  const error = new Error(message || "Session restoration failed.");
  error.code = code;
  if (status !== null) error.status = status;
  return error;
}

function createAuthResumeSupersededError() {
  const error = createAuthResumeError("Session restoration was superseded.", {
    code: "auth_resume_superseded",
  });
  error.name = "AbortError";
  return error;
}

function isAuthResumeSupersededError(error) {
  return error?.code === "auth_resume_superseded";
}

function isProtectedBusinessWorkspacePath(pathname) {
  if (!pathname || !pathname.startsWith("/business/")) return false;
  if (isPublicBusinessPath(pathname) || isBootstrapOnboardingPath(pathname)) {
    return false;
  }
  return true;
}

function isBusinessContextRelevant() {
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  return isProtectedBusinessWorkspacePath(pathname);
}

function shouldRefreshResumeSession(session) {
  if (!session) return false;
  const expiresAtSeconds = Number(session.expires_at);
  if (!Number.isFinite(expiresAtSeconds) || expiresAtSeconds <= 0) {
    return false;
  }
  return expiresAtSeconds * 1000 <= Date.now() + AUTH_RESUME_REFRESH_SKEW_MS;
}

function assertCurrentAuthResumeAttempt({
  attemptId,
  revision,
  generation,
  userId,
  signal,
  authorityRevision,
}) {
  if (signal?.aborted && signal.reason?.code === "auth_resume_timeout") {
    throw signal.reason;
  }
  if (
    signal?.aborted ||
    authStore.resumeAttemptId !== attemptId ||
    authStore.state.authResumeRevision !== revision ||
    authStore.sessionGeneration !== generation ||
    (Number.isFinite(authorityRevision) &&
      authStore.state.authorityRevision !== authorityRevision) ||
    authStore.state.user?.id !== userId ||
    authStore.loggingOut ||
    authStore.logoutRedirectInFlight ||
    authStore.terminalSessionRejected
  ) {
    throw createAuthResumeSupersededError();
  }
}

async function readResumeSession(resumeAttempt) {
  const auth = authStore.supabase?.auth;
  if (!auth?.getSession) return authStore.state.session ?? null;

  let sessionResult = null;
  let browserSessionCheckSucceeded = false;
  logAuthConsistency("recovery:session_check_started", {
    attemptId: resumeAttempt.attemptId,
    generation: resumeAttempt.generation,
  });
  try {
    sessionResult = await withTimeout(
      auth.getSession(),
      AUTH_RESUME_STEP_TIMEOUT_MS,
      "Session validation timed out"
    );
    browserSessionCheckSucceeded = !sessionResult?.error;
    if (browserSessionCheckSucceeded) {
      logAuthConsistency("recovery:session_check_succeeded", {
        attemptId: resumeAttempt.attemptId,
        generation: resumeAttempt.generation,
      });
    }
  } catch (error) {
    logAuthConsistency("recovery:session_check_failed", {
      attemptId: resumeAttempt.attemptId,
      generation: resumeAttempt.generation,
      reasonCode: error?.code || error?.name || "unknown_error",
    });
  }
  assertCurrentAuthResumeAttempt(resumeAttempt);
  if (sessionResult?.error) {
    logAuthConsistency("resume:browser_session_error_deferred_to_server", {
      error:
        sessionResult.error.name ||
        sessionResult.error.code ||
        "session_validation_failed",
    });
  }

  let session = browserSessionCheckSucceeded
    ? sessionResult?.data?.session ?? null
    : null;
  if (
    browserSessionCheckSucceeded &&
    shouldRefreshResumeSession(session) &&
    typeof auth.refreshSession === "function"
  ) {
    let refreshResult = null;
    let browserRefreshSucceeded = false;
    logAuthConsistency("recovery:refresh_started", {
      attemptId: resumeAttempt.attemptId,
      generation: resumeAttempt.generation,
    });
    try {
      refreshResult = await withTimeout(
        auth.refreshSession(),
        AUTH_RESUME_STEP_TIMEOUT_MS,
        "Session refresh timed out"
      );
      browserRefreshSucceeded = !refreshResult?.error;
      if (browserRefreshSucceeded) {
        logAuthConsistency("recovery:refresh_succeeded", {
          attemptId: resumeAttempt.attemptId,
          generation: resumeAttempt.generation,
        });
      }
    } catch (error) {
      logAuthConsistency("recovery:refresh_failed", {
        attemptId: resumeAttempt.attemptId,
        generation: resumeAttempt.generation,
        reasonCode: error?.code || error?.name || "unknown_error",
      });
    }
    assertCurrentAuthResumeAttempt(resumeAttempt);
    if (refreshResult?.error) {
      logAuthConsistency("resume:browser_refresh_error_deferred_to_server", {
        error:
          refreshResult.error.name ||
          refreshResult.error.code ||
          "session_refresh_failed",
      });
    }
    session = browserRefreshSucceeded
      ? refreshResult?.data?.session ?? null
      : null;

    // The shared Supabase refresh guard can intentionally suppress a duplicate
    // refresh with a null result. Re-read the current session instead of
    // interpreting that null as a signed-out browser.
    if (browserRefreshSucceeded && !refreshResult?.data?.session) {
      let recheckResult = null;
      let browserRecheckSucceeded = false;
      try {
        recheckResult = await withTimeout(
          auth.getSession(),
          AUTH_RESUME_STEP_TIMEOUT_MS,
          "Session validation timed out"
        );
        browserRecheckSucceeded = !recheckResult?.error;
      } catch (error) {
        logAuthConsistency("resume:browser_session_recheck_deferred_to_server", {
          error: error?.name || error?.code || "unknown_error",
        });
      }
      assertCurrentAuthResumeAttempt(resumeAttempt);
      if (recheckResult?.error) {
        logAuthConsistency("resume:browser_session_recheck_error", {
          error:
            recheckResult.error.name ||
            recheckResult.error.code ||
            "session_validation_failed",
        });
      }
      session = browserRecheckSucceeded
        ? recheckResult?.data?.session ?? null
        : null;
    }
  }

  if (
    session &&
    (!session.user?.id || session.user.id === resumeAttempt.userId)
  ) {
    updateAuthState({ session });
  } else if (session?.user?.id) {
    logAuthConsistency("resume:browser_identity_deferred_to_server");
  }
  return session;
}

async function syncResumeSessionToServer(session, resumeAttempt) {
  const tokenPair =
    session?.access_token && session?.refresh_token
      ? {
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        }
      : {};
  logAuthConsistency("recovery:server_sync_started", {
    attemptId: resumeAttempt.attemptId,
    generation: resumeAttempt.generation,
  });
  const { response, payload } = await fetchJsonWithTimeout("/api/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    cache: "no-store",
    body: JSON.stringify(tokenPair),
    signal: resumeAttempt.signal,
    timeoutMs: AUTH_RESUME_STEP_TIMEOUT_MS,
  });
  assertCurrentAuthResumeAttempt(resumeAttempt);
  if (
    response.status === 401 ||
    (response.ok && response.headers.get("x-auth-refresh-user") !== "1") ||
    (response.status === 409 &&
      payload?.error === "session_normalization_required")
  ) {
    logAuthConsistency("recovery:server_sync_rejected", {
      attemptId: resumeAttempt.attemptId,
      generation: resumeAttempt.generation,
      reasonCode:
        payload?.error ||
        (response.status === 401 ? "confirmed_guest" : "normalization_required"),
    });
    return { serverHasUser: false };
  }
  if (!response.ok || response.headers.get("x-auth-refresh-user") !== "1") {
    logAuthConsistency("recovery:server_sync_failed", {
      attemptId: resumeAttempt.attemptId,
      generation: resumeAttempt.generation,
      reasonCode: payload?.error || "server_session_sync_failed",
    });
    throw createAuthResumeError(
      payload?.error || "Server session synchronization failed.",
      {
        code: payload?.error || "server_session_sync_failed",
        status: response.status,
      }
    );
  }
  logAuthConsistency("recovery:server_sync_succeeded", {
    attemptId: resumeAttempt.attemptId,
    generation: resumeAttempt.generation,
  });
  return { serverHasUser: true };
}

async function reconcileResumeAccount(resumeAttempt) {
  let result = null;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    result = await reconcileClientAuthWithServer(
      attempt === 0 ? "pwa_resume" : "pwa_resume_retry",
      {
        signal: resumeAttempt.signal,
        bypassUncertainCooldown: true,
      }
    );
    if (
      result?.result === "authenticated" &&
      result.userId &&
      result.userId !== resumeAttempt.userId &&
      isSameResumeAttemptIgnoringGeneration(resumeAttempt) &&
      authStore.state.authStatus === "authenticated" &&
      authStore.state.user?.id === result.userId &&
      !authStore.loggingOut &&
      !authStore.logoutRedirectInFlight &&
      !authStore.terminalSessionRejected
    ) {
      resumeAttempt.userId = result.userId;
      resumeAttempt.generation = authStore.sessionGeneration;
    }
    assertCurrentAuthResumeAttempt(resumeAttempt);
    if (result?.result !== "aborted") break;
    await Promise.resolve();
  }

  if (result?.result === "authenticated") return result;
  if (result?.result === "confirmed_guest") {
    applySignedOutState("auth_resume_confirmed_guest", {
      resetGuardState: false,
      clearAuthSuppression: true,
      extraState: {
        lastAuthEvent: "SIGNED_OUT",
        serverAuthAuthority: "confirmed_guest",
      },
    });
    return result;
  }
  if (result?.result === "aborted") {
    throw createAuthResumeSupersededError();
  }
  throw createAuthResumeError(
    "Session confirmation is temporarily unavailable.",
    {
      code: result?.failureCategory || "server_reconciliation_failed",
    }
  );
}

const REVOKED_BUSINESS_CONTEXT_CODES = new Set([
  "FORBIDDEN",
  "BUSINESS_ROW_MISSING",
  "BUSINESS_NOT_FOUND",
]);

async function fetchAuthoritativeBusinessContext(resumeAttempt) {
  const { response, payload } = await fetchJsonWithTimeout(
    "/api/business/context",
    {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      signal: resumeAttempt.signal,
      timeoutMs: AUTH_RESUME_STEP_TIMEOUT_MS,
      headers: {
        "x-yb-auth-resume-revision": String(resumeAttempt.revision),
      },
    }
  );
  assertCurrentAuthResumeAttempt(resumeAttempt);

  if (response.ok && payload?.authorized === true && payload?.business?.id) {
    return {
      status: "authorized",
      business: payload.business,
      accountContext: payload.accountContext ?? null,
      role: payload.role ?? null,
    };
  }
  const denialCode = String(payload?.code || "").trim().toUpperCase();
  if (
    payload?.authorized === false &&
    (response.status === 404 ||
      (response.status === 403 &&
        REVOKED_BUSINESS_CONTEXT_CODES.has(denialCode)))
  ) {
    return {
      status: "unauthorized",
      business: null,
      accountContext: payload?.accountContext ?? null,
      role: payload?.role ?? null,
    };
  }

  throw createAuthResumeError(
    payload?.error || "Business access could not be restored.",
    {
      code: payload?.code || "business_context_failed",
      status: response.status,
    }
  );
}

function isSameResumeAttemptIgnoringGeneration({ attemptId, revision }) {
  return (
    authStore.resumeAttemptId === attemptId &&
    authStore.state.authResumeRevision === revision
  );
}

async function runAuthResumeTransaction(
  trigger = "manual",
  { startDelayMs = 0, restartCount = 0, transientRetryAttempt = 0 } = {}
) {
  const userId = authStore.state.user?.id || null;
  if (
    !userId ||
    authStore.state.authStatus !== "authenticated" ||
    authStore.state.supportModeActive ||
    authStore.loggingOut ||
    authStore.logoutRedirectInFlight ||
    authStore.terminalSessionRejected
  ) {
    return { result: "skipped" };
  }

  const attemptId = authStore.resumeAttemptId + 1;
  const revision = authStore.state.authResumeRevision + 1;
  const generation = authStore.sessionGeneration;
  const businessRelevantAtStart = isBusinessContextRelevant();
  const abortController = new AbortController();
  authStore.resumeAttemptId = attemptId;
  authStore.resumeAbortController = abortController;
  const resumeAttempt = {
    attemptId,
    revision,
    generation,
    userId,
    signal: abortController.signal,
  };
  updateAuthState({
    authResumeStatus: "restoring",
    authResumeRevision: revision,
    authResumeError: null,
    businessContextStatus: businessRelevantAtStart
      ? "resolving"
      : authStore.state.business
        ? "authorized"
        : "idle",
  });
  logAuthConsistency("recovery:start", {
    recoveryKind: "resume",
    trigger,
    attemptId,
    revision,
    generation,
    retryAttempt: transientRetryAttempt,
  });

  const deadlineTimer = window.setTimeout(() => {
    const error = createAuthResumeError("Session restoration timed out.", {
      code: "auth_resume_timeout",
    });
    error.name = "TimeoutError";
    abortController.abort(error);
  }, AUTH_RESUME_DEADLINE_MS);
  let serverAccountRestored = false;

  try {
    if (startDelayMs > 0) {
      await waitForAuthRetry(startDelayMs, resumeAttempt.signal);
      assertCurrentAuthResumeAttempt(resumeAttempt);
    }
    const session = await readResumeSession(resumeAttempt);
    assertCurrentAuthResumeAttempt(resumeAttempt);
    await syncResumeSessionToServer(session, resumeAttempt);
    assertCurrentAuthResumeAttempt(resumeAttempt);
    const reconciliation = await reconcileResumeAccount(resumeAttempt);
    if (reconciliation?.result === "confirmed_guest") {
      return reconciliation;
    }
    if (
      session?.user?.id &&
      session.user.id === authStore.state.user?.id
    ) {
      updateAuthState({ session });
    }
    assertCurrentAuthResumeAttempt(resumeAttempt);
    serverAccountRestored = true;
    resumeAttempt.authorityRevision = authStore.state.authorityRevision;

    const businessRelevant = isBusinessContextRelevant();
    if (businessRelevant) {
      if (authStore.state.businessContextStatus !== "resolving") {
        updateAuthState({ businessContextStatus: "resolving" });
      }
      const businessContext = await fetchAuthoritativeBusinessContext(
        resumeAttempt
      );
      assertCurrentAuthResumeAttempt(resumeAttempt);
      if (businessContext.status === "authorized") {
        const serverContexts =
          authStore.state.accountContext?.businessAccessContexts;
        const endpointContexts =
          businessContext.accountContext?.businessAccessContexts;
        updateAuthState({
          business: businessContext.business,
          accountContext: {
            ...(authStore.state.accountContext || {}),
            ...(businessContext.accountContext || {}),
            businessAccessContexts:
              Array.isArray(serverContexts) && serverContexts.length > 0
                ? serverContexts
                : endpointContexts || [],
          },
          role: resolveRole(
            authStore.state.profile,
            authStore.state.user,
            businessContext.role || authStore.state.role
          ),
          businessContextStatus: "authorized",
        });
      } else {
        updateAuthState({
          business: null,
          accountContext: {
            ...(authStore.state.accountContext || {}),
            businessRowExists: false,
            hasBusinessAccess: false,
            businessAccessContexts: [],
          },
          businessContextStatus: "unauthorized",
        });
        authStore.businessPromise = null;
        authStore.businessUserId = null;
      }
    } else {
      updateAuthState({
        businessContextStatus: authStore.state.business
          ? "authorized"
          : "idle",
      });
    }

    assertCurrentAuthResumeAttempt(resumeAttempt);
    updateAuthState({
      authResumeStatus: "ready",
      authResumeError: null,
      error: null,
      lastError: null,
    });
    authStore.lastAuthenticatedAuthorityAt = Date.now();
    if (typeof navigator === "undefined" || navigator.onLine !== false) {
      authStore.resumeWasOffline = false;
    }
    logAuthConsistency("recovery:succeeded", {
      recoveryKind: "resume",
      trigger,
      attemptId,
      revision,
      generation,
    });
    return { result: "ready", revision };
  } catch (error) {
    if (isAuthResumeSupersededError(error)) {
      const sameAuthenticatedUser =
        isSameResumeAttemptIgnoringGeneration(resumeAttempt) &&
        authStore.state.authStatus === "authenticated" &&
        authStore.state.user?.id === resumeAttempt.userId &&
        !authStore.loggingOut &&
        !authStore.logoutRedirectInFlight &&
        !authStore.terminalSessionRejected;
      if (sameAuthenticatedUser && restartCount < 1) {
        updateAuthState({
          authResumeStatus: "restoring",
          authResumeError: null,
          businessContextStatus: isBusinessContextRelevant()
            ? "resolving"
            : authStore.state.business
              ? "authorized"
              : "idle",
        });
        logAuthConsistency("resume:restart_scheduled", {
          trigger,
          attemptId,
          revision,
        });
        return {
          result: "superseded",
          revision,
          restart: true,
          restartCount: restartCount + 1,
        };
      }
      if (sameAuthenticatedUser) {
        const restartError = createAuthResumeError(
          "Session changed again while restoration was in progress.",
          { code: "auth_resume_repeatedly_superseded" }
        );
        updateAuthState({
          authResumeStatus: "error",
          authResumeError: restartError,
          businessContextStatus: isBusinessContextRelevant()
            ? "error"
            : authStore.state.business
              ? "authorized"
              : "idle",
          error: restartError,
          lastError: restartError.message,
        });
      }
      logAuthConsistency("resume:superseded", {
        trigger,
        attemptId,
        revision,
      });
      return { result: "superseded", revision };
    }
    const browserOffline =
      typeof navigator !== "undefined" && navigator.onLine === false;
    const retryDelay =
      AUTH_RESUME_TRANSIENT_RETRY_DELAYS_MS[transientRetryAttempt];
    const retryable =
      !browserOffline &&
      Number.isFinite(retryDelay) &&
      error?.code !== "auth_resume_timeout";
    const waitingForOnline = browserOffline;
    if (waitingForOnline) authStore.resumeWasOffline = true;
    if (isSameResumeAttemptIgnoringGeneration(resumeAttempt)) {
      updateAuthState({
        authReadiness:
          authStore.state.user?.id &&
          authStore.state.authStatus === "authenticated"
            ? "ready"
            : authStore.state.authReadiness,
        authResumeStatus:
          retryable || waitingForOnline
            ? "restoring"
            : serverAccountRestored
              ? "ready"
              : "error",
        authResumeError: retryable || waitingForOnline ? null : error,
        businessContextStatus: isBusinessContextRelevant()
          ? retryable || waitingForOnline
            ? "resolving"
            : "error"
          : authStore.state.business
            ? "authorized"
            : "idle",
        error,
        lastError:
          error?.message ||
          "Your session could not be restored. Check your connection and retry.",
      });
    }
    logAuthConsistency(
      retryable || waitingForOnline
        ? "recovery:retry_pending"
        : "recovery:terminal_failure",
      {
        recoveryKind: "resume",
        trigger,
        attemptId,
        revision,
        generation,
        reasonCode: error?.code || error?.name || "unknown_error",
        waitingForOnline,
      }
    );
    return {
      result: "error",
      revision,
      error,
      retryable,
      retryDelay,
      waitingForOnline,
    };
  } finally {
    window.clearTimeout(deadlineTimer);
    if (authStore.resumeAbortController === abortController) {
      authStore.resumeAbortController = null;
    }
  }
}

function requestAuthResume(trigger = "manual", options = {}) {
  if (authStore.resumePromise) return authStore.resumePromise;
  let resumePromise;
  resumePromise = runAuthResumeTransaction(trigger, options).finally(() => {
    if (authStore.resumePromise === resumePromise) {
      authStore.resumePromise = null;
    }
  });
  authStore.resumePromise = resumePromise;
  void resumePromise.then((result) => {
    if (
      result?.restart === true &&
      authStore.state.authResumeStatus === "restoring" &&
      authStore.state.user?.id &&
      authStore.state.authStatus === "authenticated" &&
      !authStore.resumePromise
    ) {
      void requestAuthResume("session_generation_restart", {
        restartCount: result.restartCount,
      });
      return;
    }
    if (result?.result === "ready" || result?.result === "confirmed_guest") {
      if (authStore.resumeRetryTimer) {
        window.clearTimeout(authStore.resumeRetryTimer);
        authStore.resumeRetryTimer = null;
      }
      return;
    }
    if (
      result?.result === "error" &&
      result.retryable === true &&
      authStore.state.user?.id &&
      authStore.state.authStatus === "authenticated" &&
      !authStore.terminalSessionRejected &&
      (typeof navigator === "undefined" || navigator.onLine !== false)
    ) {
      const retryAttempt = Number(options.transientRetryAttempt) || 0;
      const retryDelay = result.retryDelay;
      if (Number.isFinite(retryDelay) && !authStore.resumeRetryTimer) {
        authStore.resumeRetryTimer = window.setTimeout(() => {
          authStore.resumeRetryTimer = null;
          if (
            !authStore.state.user?.id ||
            authStore.state.authStatus !== "authenticated" ||
            authStore.terminalSessionRejected
          ) {
            return;
          }
          void requestAuthResume("transient_retry", {
            transientRetryAttempt: retryAttempt + 1,
          });
        }, retryDelay);
        logAuthConsistency("resume:transient_retry_scheduled", {
          trigger,
          retryAttempt: retryAttempt + 1,
          retryDelay,
        });
      }
    }
  });
  return resumePromise;
}

function scheduleAuthResume(trigger) {
  if (typeof window === "undefined") return;
  if (authStore.resumeRetryTimer) {
    window.clearTimeout(authStore.resumeRetryTimer);
    authStore.resumeRetryTimer = null;
  }
  authStore.resumeWasBackgrounded = false;
  authStore.resumeBackgroundedAt = 0;
  // Publish the restoring barrier synchronously on the first foreground signal.
  // The short delay coalesces the visibility/focus/pageshow burst before any
  // network work begins, so protected consumers cannot race through the gap.
  void requestAuthResume(trigger, {
    startDelayMs: AUTH_RESUME_SIGNAL_COALESCE_MS,
  });
}

function ensureAuthResumeLifecycle() {
  if (typeof window === "undefined" || authStore.resumeLifecycleCleanup) return;

  const markBackgrounded = () => {
    if (!authStore.resumeWasBackgrounded) {
      authStore.resumeBackgroundedAt = Date.now();
    }
    authStore.resumeWasBackgrounded = true;
  };
  const shouldSkipShortBackgroundResume = () => {
    const backgroundedAt = authStore.resumeBackgroundedAt;
    const backgroundedFor = backgroundedAt
      ? Math.max(0, Date.now() - backgroundedAt)
      : Infinity;
    const authorityAge = authStore.lastAuthenticatedAuthorityAt
      ? Math.max(0, Date.now() - authStore.lastAuthenticatedAuthorityAt)
      : Infinity;
    return (
      backgroundedFor < AUTH_RESUME_SHORT_BACKGROUND_GRACE_MS &&
      authorityAge < AUTH_RESUME_MAX_FRESH_AUTHORITY_AGE_MS &&
      authStore.state.authStatus === "authenticated" &&
      authStore.state.serverAuthAuthority === "authenticated" &&
      Boolean(authStore.state.session) &&
      !shouldRefreshResumeSession(authStore.state.session) &&
      !authStore.resumeWasOffline
    );
  };
  const resumeAfterBackground = (trigger) => {
    if (!authStore.resumeWasBackgrounded) return;
    if (shouldSkipShortBackgroundResume()) {
      authStore.resumeWasBackgrounded = false;
      authStore.resumeBackgroundedAt = 0;
      logAuthConsistency("resume:short_background_skipped", { trigger });
      return;
    }
    scheduleAuthResume(trigger);
  };
  const handleVisibility = () => {
    if (document.visibilityState === "hidden") {
      markBackgrounded();
      return;
    }
    resumeAfterBackground("visibility");
  };
  const handlePageShow = (event) => {
    if (event?.persisted) {
      scheduleAuthResume(event?.persisted ? "pageshow_persisted" : "pageshow");
      return;
    }
    resumeAfterBackground("pageshow");
  };
  const handleFocus = () => {
    resumeAfterBackground("focus");
  };
  const handleResume = () => {
    resumeAfterBackground("resume");
  };
  const handleOffline = () => {
    authStore.resumeWasOffline = true;
    if (!authStore.state.user?.id) {
      authStore.bootstrapWaitingForOnline = true;
    }
    logAuthConsistency("recovery:offline_detected", {
      recoveryKind: authStore.state.user?.id ? "resume" : "bootstrap",
    });
  };
  const handleOnline = () => {
    const bootstrapWaitingForOnline = authStore.bootstrapWaitingForOnline;
    authStore.bootstrapWaitingForOnline = false;
    const shouldResume =
      authStore.resumeWasOffline ||
      authStore.resumeWasBackgrounded ||
      authStore.state.authResumeStatus === "error" ||
      authStore.state.businessContextStatus === "error";
    authStore.resumeWasOffline = false;
    if (
      bootstrapWaitingForOnline &&
      !authStore.state.user?.id &&
      !authStore.bootstrapPromise
    ) {
      if (authStore.bootstrapRetryTimer) {
        window.clearTimeout(authStore.bootstrapRetryTimer);
        authStore.bootstrapRetryTimer = null;
      }
      void bootstrapAuth({
        trigger: "connectivity_restored",
        transientRetryAttempt: 0,
      });
    }
    if (!shouldResume) return;
    if (authStore.resumeRetryTimer) {
      window.clearTimeout(authStore.resumeRetryTimer);
      authStore.resumeRetryTimer = null;
    }
    const inFlightResume = authStore.resumePromise;
    if (!inFlightResume) {
      scheduleAuthResume("online");
      return;
    }
    void inFlightResume.finally(() => {
      if (
        (authStore.state.authResumeStatus === "error" ||
          authStore.state.businessContextStatus === "error") &&
        authStore.state.user?.id &&
        authStore.state.authStatus === "authenticated"
      ) {
        void requestAuthResume("online_retry");
      }
    });
  };

  if (document.visibilityState === "hidden") {
    authStore.resumeWasBackgrounded = true;
  }
  document.addEventListener("visibilitychange", handleVisibility);
  window.addEventListener("pagehide", markBackgrounded);
  window.addEventListener("pageshow", handlePageShow);
  window.addEventListener("focus", handleFocus);
  window.addEventListener("offline", handleOffline);
  window.addEventListener("online", handleOnline);
  document.addEventListener("freeze", markBackgrounded);
  document.addEventListener("resume", handleResume);

  authStore.resumeLifecycleCleanup = () => {
    document.removeEventListener("visibilitychange", handleVisibility);
    window.removeEventListener("pagehide", markBackgrounded);
    window.removeEventListener("pageshow", handlePageShow);
    window.removeEventListener("focus", handleFocus);
    window.removeEventListener("offline", handleOffline);
    window.removeEventListener("online", handleOnline);
    document.removeEventListener("freeze", markBackgrounded);
    document.removeEventListener("resume", handleResume);
    if (authStore.resumeRetryTimer) {
      window.clearTimeout(authStore.resumeRetryTimer);
      authStore.resumeRetryTimer = null;
    }
    if (authStore.bootstrapRetryTimer) {
      window.clearTimeout(authStore.bootstrapRetryTimer);
      authStore.bootstrapRetryTimer = null;
    }
  };
}

function releaseAuthResumeLifecycle() {
  authStore.resumeLifecycleCleanup?.();
  authStore.resumeLifecycleCleanup = null;
}

async function applyUserUpdate(user) {
  if (authStore.state.supportModeActive) {
    return;
  }
  if (authStore.terminalSessionRejected && user) {
    logAuthConsistency("user:update:ignored_terminal_rejection");
    return;
  }
  const currentId = authStore.state.user?.id || null;
  const nextId = user?.id || null;
  const nextStatus = user ? "authenticated" : "unauthenticated";
  const statusChanged = authStore.state.authStatus !== nextStatus;
  const needsProfile =
    Boolean(user) &&
    (!authStore.state.profile || authStore.profileUserId !== nextId);

  if (currentId === nextId && !statusChanged && !needsProfile) {
    const mergedUser = mergeAuthUser(authStore.state.user, user);
    const currentAvatar = resolveAvatarUrl(authStore.state.user?.user_metadata);
    const mergedAvatar = resolveAvatarUrl(mergedUser?.user_metadata);
    if (mergedUser && mergedAvatar && mergedAvatar !== currentAvatar) {
      updateAuthState({
        user: mergedUser,
        role: resolveRole(authStore.state.profile, mergedUser, authStore.state.role),
      });
    }
    return;
  }

  if (!user) {
    applySignedOutState("apply_user_update", {
      resetGuardState: false,
      clearAuthSuppression: true,
    });
    return;
  }

  if (currentId !== nextId) {
    authStore.businessPromise = null;
    authStore.businessUserId = null;
  }

  if (currentId !== nextId || statusChanged) {
    const mergedUser = mergeAuthUser(authStore.state.user, user);
    const retainedProfile =
      currentId === nextId ? keepProfileForUser(authStore.state.profile, mergedUser) : null;
    updateAuthAuthority({
      ...withGuardState(buildSignedInState({ user: mergedUser })),
      profile: retainedProfile,
      business: currentId === nextId ? authStore.state.business : null,
      role: resolveRole(retainedProfile, mergedUser, authStore.state.role),
    });
    logAuthConsistency("user:update:accepted", {
      nextUserId: mergedUser?.id ?? null,
      retainedProfile: Boolean(retainedProfile),
      statusChanged,
    });
  }

  const { profile, accountContext, error: profileError } = await getProfileForUser(user);
  if (authStore.state.user?.id !== user.id) return;
  if (profileError && !profile) {
    logAuthConsistency("profile:fetch:deferred", {
      userId: user.id,
      error: profileError?.message || String(profileError),
    });
    updateAuthState({
      authStatus: "authenticated",
      user: mergeAuthUser(authStore.state.user, user),
      accountContext: accountContext ?? authStore.state.accountContext,
      role: resolveRole(authStore.state.profile, authStore.state.user || user, authStore.state.role),
    });
    return;
  }
  const accountStatus = normalizeAccountStatus(profile?.account_status);
  if (isBlockedAccountStatus(accountStatus)) {
    await rejectTerminalSession("account_lifecycle_blocked");
    applySignedOutState("account_lifecycle_blocked", {
      resetGuardState: true,
      clearAuthSuppression: true,
      extraState: {
        lastAuthEvent: "SIGNED_OUT",
        lastError: "This account is no longer available.",
      },
    });
    if (typeof window !== "undefined") {
      const currentPath = window.location.pathname;
      if (!isPublicLoginSurfacePath(currentPath) && !shouldSuppressAuthUiReset()) {
        redirectWithGuard(getAccountDeletedRedirectPath());
      }
    }
    return;
  }
  const previousProfile = keepProfileForUser(authStore.state.profile, user);
  const fetchedProfile = keepProfileForUser(profile, user);
  const nextProfile = fetchedProfile
    ? mergeFreshProfile(previousProfile, fetchedProfile, authStore.state.user || user)
    : previousProfile;
  const nextUser = mergeAuthUser(authStore.state.user, user);
  const nextRole = resolveRole(nextProfile, nextUser, authStore.state.role);
  let business = null;
  if (nextRole === "business") {
    const { business: businessRow } = await getBusinessForUser(user);
    if (authStore.state.user?.id !== user.id) return;
    business = mergeBusinessEnrichmentState(
      authStore.state.business,
      businessRow,
      user,
      authStore.state.businessContextStatus
    );
  } else {
    authStore.businessPromise = null;
    authStore.businessUserId = null;
  }
  updateAuthState({
    authStatus: "authenticated",
    user: nextUser,
    profile: nextProfile,
    business,
    accountContext: accountContext ?? authStore.state.accountContext,
    role: nextRole,
  });
  logAuthConsistency("profile:merged", {
    userId: nextUser?.id ?? null,
    hasProfile: Boolean(nextProfile),
    avatarSource: resolveAvatarUrl(
      nextProfile?.profile_photo_url,
      nextUser?.user_metadata
    )
      ? "resolved"
      : "none",
  });
}

async function handleMissingUserAuthEvent(event) {
  const isMissingUserEvent = event === "SIGNED_OUT" || event === "USER_DELETED";
  if (isMissingUserEvent) {
    const explicitLogout =
      authStore.loggingOut || authStore.logoutRedirectInFlight;
    if (
      event === "SIGNED_OUT" &&
      !explicitLogout &&
      !authStore.terminalSessionRejected
    ) {
      const reconciliation = await reconcileClientAuthWithServer("signed_out");
      if (
        reconciliation.result === "authenticated" ||
        reconciliation.result === "uncertain" ||
        reconciliation.result === "aborted"
      ) {
        return;
      }
    }
    if (!shouldSuppressAuthUiReset()) {
      emitAuthUiReset("auth_event:signed_out");
    }
    clearVerifiedUserCache();
    applySignedOutState("auth_event:signed_out", {
      resetGuardState: true,
      clearAuthSuppression: false,
      extraState: {
        lastAuthEvent: event,
        lastError: null,
        serverAuthAuthority: "confirmed_guest",
      },
    });
    return;
  }

  if (authStore.terminalSessionRejected) {
    logAuthConsistency("auth_event:no_user:ignored_terminal_rejection", { event });
    return;
  }

  if (
    AUTH_EVENT_NO_USER_VERIFY_EVENTS.has(event) &&
    (authStore.bootstrapPromise || !authStore.state.authInitialized)
  ) {
    logAuthConsistency("auth_event:no_user:bootstrap_wait", { event });
    return;
  }

  try {
    const { data, error } = await authStore.supabase.auth.getSession();
    const session = data?.session ?? null;
    if (session?.user) {
      logAuthConsistency("auth_event:no_user:session_recovered", {
        event,
        recoveredUserId: session.user.id,
        error: error?.message ?? null,
      });
      updateAuthState({ session, authInitialized: true, lastAuthEvent: event });
      await applyUserUpdate(session.user);
      return;
    }
  } catch (error) {
    logAuthConsistency("auth_event:no_user:verify_failed", {
      event,
      error: error?.message || String(error),
    });
    if (authStore.state.user?.id) return;
  }

  if (authStore.state.user?.id && authStore.state.authStatus === "authenticated") {
    logAuthConsistency("auth_event:no_user:ignored_authenticated", { event });
    return;
  }

  if (isAuthHandoffUrl()) {
    const serverSnapshot = await fetchServerAccountSnapshotWithHandoffRetry(
      `auth_event_no_user_${event || "unknown"}`
    );
    if (serverSnapshot.user?.id) {
      const nextUser = mergeAuthUser(authStore.state.user, serverSnapshot.user);
      const nextProfile = serverSnapshot.profile
        ? mergeFreshProfile(
            keepProfileForUser(authStore.state.profile, nextUser),
            serverSnapshot.profile,
            nextUser
          )
        : keepProfileForUser(authStore.state.profile, nextUser);
      updateAuthAuthority({
        ...withGuardState(buildSignedInState({ user: nextUser })),
        profile: nextProfile,
        accountContext: serverSnapshot.accountContext ?? authStore.state.accountContext,
        role: resolveRole(nextProfile, nextUser, serverSnapshot.role),
        authInitialized: true,
        error: null,
        lastAuthEvent: event,
      });
      logAuthConsistency("auth_event:no_user:handoff_recovered", {
        event,
        userId: nextUser?.id || null,
      });
      return;
    }
  }

  applySignedOutState("auth_event:no_user", {
    resetGuardState: false,
    clearAuthSuppression: true,
  });
}

async function bootstrapAuth({
  trigger = "initial_hydration",
  transientRetryAttempt = 0,
} = {}) {
  if (authStore.bootstrapPromise) return authStore.bootstrapPromise;

  if (authStore.terminalSessionRejected) {
    applySignedOutState("bootstrap:terminal_rejection", {
      resetGuardState: true,
      clearAuthSuppression: true,
    });
    updateAuthState({ authInitialized: true, authReadiness: "ready" });
    logAuthConsistency("bootstrap:skipped_terminal_rejection");
    return Promise.resolve();
  }

  const bootstrapStartedAt = Date.now();
  const retryDelay =
    AUTH_BOOTSTRAP_TRANSIENT_RETRY_DELAYS_MS[transientRetryAttempt];
  const browserOffline =
    typeof navigator !== "undefined" && navigator.onLine === false;
  const canAutoRetry = Number.isFinite(retryDelay) && !browserOffline;
  let recoveryFailure = null;
  const publishRecoveryFailure = (error, reasonCode) => {
    recoveryFailure = { error, reasonCode };
    authStore.bootstrapWaitingForOnline = browserOffline;
    applyRecoverableAuthState(error, reasonCode, {
      terminal: !canAutoRetry && !browserOffline,
    });
  };
  const abortController = new AbortController();
  authStore.bootstrapAbortController = abortController;
  const { signal } = abortController;
  const deadlineTimer = window.setTimeout(() => {
    const error = new Error("Session recovery took too long.");
    error.name = "TimeoutError";
    abortController.abort(error);
  }, AUTH_BOOTSTRAP_DEADLINE_MS);

  const bootstrapPromise = (async () => {
    const keepUsableState =
      authStore.state.authInitialized &&
      authStore.state.authReadiness === "recoverable_error";
    const keepAuthoritativeFirstRender =
      authStore.state.authInitialized &&
      authStore.state.authReadiness === "ready" &&
      ((authStore.state.authStatus === "authenticated" &&
        authStore.state.serverAuthAuthority === "authenticated") ||
        (authStore.state.authStatus === "unauthenticated" &&
          authStore.state.serverAuthAuthority === "confirmed_guest"));
    updateAuthState({
      authStatus:
        keepUsableState || keepAuthoritativeFirstRender
          ? authStore.state.authStatus
          : "loading",
      authReadiness: keepAuthoritativeFirstRender
        ? authStore.state.authReadiness
        : "initializing",
      error: null,
    });
    logAuthConsistency("recovery:start", {
      recoveryKind: "bootstrap",
      trigger,
      attemptId: transientRetryAttempt + 1,
    });
    let user = null;
    let sessionChecked = false;
    let sessionError = null;

    if (signal.aborted) {
      logAuthDiag("auth:bootstrap:aborted", { step: "start" });
      return;
    }

    if (authStore.supabase?.auth?.getSession) {
      sessionChecked = true;
      try {
        const { data, error } = await withTimeout(
          authStore.supabase.auth.getSession(),
          AUTH_SESSION_STEP_TIMEOUT_MS,
          "Session restore timed out"
        );
        if (error) {
          sessionError = error;
          setAuthError(error);
        }
        const session = data?.session ?? null;
        updateAuthState({ session });
        user = session?.user ?? null;
        logAuthConsistency("bootstrap:get_session", {
          hasSession: Boolean(session),
          userId: user?.id || null,
          error: error?.message ?? null,
        });
        logAuthDiag("auth:getSession:result", {
          ok: !error,
          hasUser: Boolean(user),
          sessionUserId: user?.id ?? null,
          error: error?.message ?? null,
        });
      } catch (err) {
        sessionError = err;
        setAuthError(err);
        if (!authStore.state.user?.id) {
          updateAuthState({ session: null });
        }
        logAuthConsistency("bootstrap:get_session:error", {
          error: err?.message ?? String(err),
        });
        logAuthDiag("auth:getSession:result", {
          ok: false,
          hasUser: false,
          sessionUserId: null,
          error: err?.message ?? String(err),
        });
      }
    }

    if (signal.aborted) {
      logAuthDiag("auth:bootstrap:aborted", { step: "session" });
      return;
    }

    if (sessionChecked) {
      if (!user || sessionError) {
        const serverSnapshot = await fetchServerAccountSnapshotWithHandoffRetry(
          sessionError ? "bootstrap_session_error" : "bootstrap_empty_session",
          signal
        );
        if (serverSnapshot.user?.id) {
          const nextUser = mergeAuthUser(authStore.state.user, serverSnapshot.user);
          const nextProfile = serverSnapshot.profile
            ? mergeFreshProfile(
                keepProfileForUser(authStore.state.profile, nextUser),
                serverSnapshot.profile,
                nextUser
              )
            : keepProfileForUser(authStore.state.profile, nextUser);
          updateAuthAuthority({
            ...withGuardState(buildSignedInState({ user: nextUser })),
            profile: nextProfile,
            accountContext: serverSnapshot.accountContext ?? authStore.state.accountContext,
            role: resolveRole(nextProfile, nextUser, serverSnapshot.role),
            authInitialized: true,
            authReconciliationStatus: "idle",
            serverAuthAuthority: "authenticated",
            error: null,
          });
          logAuthConsistency("bootstrap:server_snapshot:published", {
            userId: nextUser?.id || null,
            hasProfile: Boolean(nextProfile?.id),
            role: authStore.state.role || null,
          });
          return;
        }
        if (serverSnapshot.confirmedGuest) {
          applySignedOutState("bootstrap:confirmed_guest", {
            resetGuardState: false,
            clearAuthSuppression: true,
            extraState: {
              serverAuthAuthority: "confirmed_guest",
            },
          });
          updateAuthState({ authInitialized: true, authReadiness: "ready" });
          return;
        }
        if (serverSnapshot.error) {
          publishRecoveryFailure(
            serverSnapshot.error,
            sessionError ? "session_and_server_snapshot_failed" : "server_snapshot_failed"
          );
          return;
        }
        if (authStore.state.user?.id && authStore.state.authStatus === "authenticated") {
          logAuthConsistency("bootstrap:no_session:kept_authenticated", {
            reason: sessionError ? "session_error" : "empty_session",
            error: sessionError?.message || null,
          });
          updateAuthState({ authInitialized: true, authReadiness: "ready" });
          return;
        }
        applySignedOutState("bootstrap:no_session", {
          resetGuardState: false,
          clearAuthSuppression: true,
        });
        updateAuthState({ authInitialized: true, authReadiness: "ready" });
        return;
      }
    }

    if (signal.aborted) {
      logAuthDiag("auth:bootstrap:aborted", { step: "verified_user" });
      return;
    }

    if (!user) {
      applySignedOutState("bootstrap:no_user", {
        resetGuardState: false,
        clearAuthSuppression: true,
      });
      updateAuthState({ authInitialized: true, authReadiness: "ready" });
      return;
    }
    try {
      await withTimeout(
        applyUserUpdate(user),
        AUTH_SESSION_STEP_TIMEOUT_MS,
        "Account profile restore timed out"
      );
    } catch (error) {
      setAuthError(error);
      if (authStore.state.user?.id) {
        logAuthConsistency("bootstrap:error:kept_authenticated", {
          error: error?.message || String(error),
        });
        publishRecoveryFailure(error, "profile_restore_failed");
        return;
      }
      publishRecoveryFailure(error, "bootstrap_failed");
    } finally {
      if (!recoveryFailure && authStore.state.authReadiness === "initializing") {
        updateAuthState({ authInitialized: true, authReadiness: "ready" });
      } else {
        updateAuthState({ authInitialized: true });
      }
      logAuthConsistency("bootstrap:end", {
        userId: authStore.state.user?.id || null,
        authStatus: authStore.state.authStatus,
      });
    }
  })().finally(() => {
    window.clearTimeout(deadlineTimer);
    logDataDiag("auth:bootstrap_complete", {
      durationMs: Date.now() - bootstrapStartedAt,
      readiness: authStore.state.authReadiness,
      status: authStore.state.authStatus,
    });
    if (authStore.bootstrapAbortController === abortController) {
      authStore.bootstrapAbortController = null;
    }
    if (authStore.bootstrapPromise === bootstrapPromise) {
      authStore.bootstrapPromise = null;
    }
    if (
      recoveryFailure &&
      canAutoRetry &&
      authStore.providerCount > 0 &&
      !authStore.bootstrapRetryTimer &&
      !authStore.loggingOut &&
      !authStore.logoutRedirectInFlight &&
      !authStore.terminalSessionRejected
    ) {
      authStore.bootstrapRetryTimer = window.setTimeout(() => {
        authStore.bootstrapRetryTimer = null;
        void bootstrapAuth({
          trigger: "transient_retry",
          transientRetryAttempt: transientRetryAttempt + 1,
        });
      }, retryDelay);
      logAuthConsistency("recovery:retry_scheduled", {
        recoveryKind: "bootstrap",
        trigger,
        attemptId: transientRetryAttempt + 2,
        retryDelay,
        reasonCode: recoveryFailure.reasonCode,
      });
    } else if (recoveryFailure && browserOffline) {
      logAuthConsistency("recovery:offline_wait", {
        recoveryKind: "bootstrap",
        trigger,
        attemptId: transientRetryAttempt + 1,
        reasonCode: recoveryFailure.reasonCode,
      });
    } else if (!recoveryFailure && signal.aborted) {
      logAuthConsistency("recovery:aborted", {
        recoveryKind: "bootstrap",
        trigger,
        attemptId: transientRetryAttempt + 1,
        reasonCode: signal.reason?.name || "superseded",
      });
    } else if (!recoveryFailure) {
      authStore.bootstrapWaitingForOnline = false;
      logAuthConsistency("recovery:succeeded", {
        recoveryKind: "bootstrap",
        trigger,
        attemptId: transientRetryAttempt + 1,
      });
    }
  });
  authStore.bootstrapPromise = bootstrapPromise;
  return bootstrapPromise;
}

function ensureAuthListener() {
  if (authStore.authUnsubscribe) return;
  if (!authStore.supabase?.auth?.onAuthStateChange) return;

  const { data } = authStore.supabase.auth.onAuthStateChange(
    (event, session) => {
      const user = session?.user ?? null;
      const previousUserId = authStore.state.user?.id || null;
      let openedNewSessionGeneration = false;
      if (event === "SIGNED_IN" && authStore.terminalSessionRejected) {
        // Supabase can also emit SIGNED_IN while re-establishing a session.
        // Only an entry point that just initiated password/OAuth login may
        // release a terminally rejected session generation.
        const loginIntentId = new URL(window.location.href).searchParams.get("yb_login_intent");
        const loginIntent = loginIntentId
          ? consumeExplicitLoginIntent(loginIntentId)
          : null;
        if (loginIntent?.kind === "oauth") {
          authStore.terminalSessionRejected = false;
          setExplicitLoginCorrelationRequired(false);
          authStore.sessionGeneration += 1;
          openedNewSessionGeneration = true;
          logAuthConsistency("auth_event:new_session_generation", {
            event,
            loginKind: loginIntent.kind,
          });
        } else {
          logAuthConsistency("auth_event:signed_in_ignored_without_login_intent");
        }
        if (loginIntentId) {
          const url = new URL(window.location.href);
          url.searchParams.delete("yb_login_intent");
          window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
        }
      }
      if (
        event === "SIGNED_IN" &&
        user &&
        !authStore.terminalSessionRejected &&
        !openedNewSessionGeneration &&
        previousUserId !== user.id
      ) {
        authStore.sessionGeneration += 1;
      }
      if (authStore.terminalSessionRejected && user) {
        logAuthConsistency("auth_event:ignored_terminal_rejection", { event });
        return;
      }
      if (user) {
        if (authStore.bootstrapRetryTimer) {
          window.clearTimeout(authStore.bootstrapRetryTimer);
          authStore.bootstrapRetryTimer = null;
        }
        authStore.bootstrapWaitingForOnline = false;
        if (authStore.bootstrapPromise && authStore.bootstrapAbortController) {
          authStore.bootstrapAbortController.abort(
            new DOMException(
              "Bootstrap superseded by an authenticated auth event.",
              "AbortError"
            )
          );
          logAuthConsistency("recovery:superseded", {
            recoveryKind: "bootstrap",
            reasonCode: "authenticated_auth_event",
          });
        }
      }
      const signedInIdentityChanged =
        event === "SIGNED_IN" &&
        Boolean(previousUserId) &&
        Boolean(user?.id) &&
        previousUserId !== user.id;
      const signedInSameIdentityDuringResume =
        event === "SIGNED_IN" &&
        Boolean(previousUserId) &&
        previousUserId === user?.id &&
        authStore.state.authResumeStatus === "restoring";
      if (signedInIdentityChanged) {
        authStore.resumeAttemptId += 1;
        authStore.resumeAbortController?.abort?.();
        authStore.resumeAbortController = null;
        authStore.resumePromise = null;
      } else if (signedInSameIdentityDuringResume) {
        // Supabase deliberately re-emits SIGNED_IN for an unchanged, healthy
        // session whenever a hidden tab becomes visible. That is a session
        // observation, not a new auth generation, so it must not abort the
        // foreground transaction that the same visibility event started.
        logAuthConsistency("auth_event:same_identity_signed_in_during_resume");
      }
      const redundantSameIdentitySignedIn =
        event === "SIGNED_IN" &&
        Boolean(previousUserId) &&
        previousUserId === user?.id &&
        !openedNewSessionGeneration;
      const publishAuthEventState =
        user && !redundantSameIdentitySignedIn
          ? updateAuthAuthority
          : updateAuthState;
      publishAuthEventState({
        session,
        authInitialized: true,
        ...(signedInIdentityChanged
          ? {
              authResumeStatus: "idle",
              authResumeError: null,
              businessContextStatus: "idle",
            }
          : {}),
        ...(user
          ? {
              authReconciliationStatus: "idle",
              serverAuthAuthority: "authenticated",
            }
          : {}),
      });
      if (authDiagEnabled) {
        console.warn("[auth] state-change", {
          supportModeActive: authStore.state.supportModeActive === true,
          supabaseUserId: user?.id ?? null,
        });
      }
      logAuthDiag("auth:event", {
        event,
        hasUser: Boolean(user),
        sessionUserId: user?.id ?? null,
      });

      const processEvent = async () => {
        if (event === "SIGNED_OUT" || event === "USER_DELETED") {
          await handleMissingUserAuthEvent(event);
          return;
        }

        updateAuthState({ lastAuthEvent: event });
        if (!user) {
          await handleMissingUserAuthEvent(event);
          return;
        }
        authStore.loggingOut = false;
        if (authStore.state.supportModeActive) return;
        await applyUserUpdate(user);
        if (authStore.state.user?.id === user.id) {
          updateAuthState({
            authReadiness: "ready",
            authInitialized: true,
            error: null,
            lastError: null,
          });
        }
      };

      // Supabase invokes this callback while its auth lock is held. Defer any
      // getSession/profile work so a null auth event cannot deadlock the lock.
      queueMicrotask(() => {
        void processEvent().catch((error) => setAuthError(error));
      });
    }
  );

  authStore.authUnsubscribe = () => {
    data?.subscription?.unsubscribe();
  };

  const acceptConfirmedPasswordLogin = () => {
    const session = consumeConfirmedPasswordSession();
    if (!session?.user || !authStore.terminalSessionRejected) return;
    authStore.terminalSessionRejected = false;
    setExplicitLoginCorrelationRequired(false);
    authStore.sessionGeneration += 1;
    updateAuthState({ session, authInitialized: true, lastAuthEvent: "SIGNED_IN" });
    void applyUserUpdate(session.user);
  };
  window.addEventListener(EXPLICIT_LOGIN_CONFIRMED_EVENT, acceptConfirmedPasswordLogin);
  const previousUnsubscribe = authStore.authUnsubscribe;
  authStore.authUnsubscribe = () => {
    window.removeEventListener(EXPLICIT_LOGIN_CONFIRMED_EVENT, acceptConfirmedPasswordLogin);
    previousUnsubscribe?.();
  };
}

function releaseAuthListener() {
  if (!authStore.authUnsubscribe) return;
  authStore.authUnsubscribe();
  authStore.authUnsubscribe = null;
}

function redirectWithGuard(target, expiredSessionDiagnostic = null) {
  if (typeof window === "undefined") return;
  const current = new URL(window.location.href);
  const redirectUrl = new URL(target, window.location.origin);

  const nextPath = `${redirectUrl.pathname}${redirectUrl.search}`;
  if (`${current.pathname}${current.search}` === nextPath) {
    return;
  }
  const currentPath = window.location.pathname;
  if (currentPath && isBootstrapOnboardingPath(currentPath) && nextPath === "/") {
    if (process.env.NODE_ENV !== "production") {
      console.info("[ONBOARDING_REDIRECT_TRACE] source=redirectWithGuard_suppressed", {
        from: currentPath,
        to: nextPath,
        reason: "suppress_onboarding_to_home_redirect",
        role: authStore.state.profile?.role ?? authStore.state.role ?? null,
        userId: authStore.state.user?.id ?? null,
      });
    }
    return;
  }
  if (
    currentPath &&
    isBootstrapOnboardingPath(currentPath) &&
    isProtectedCustomerPath(redirectUrl.pathname)
  ) {
    logCustomerOnboardingNavDiag({
      source: "AuthProvider.redirectWithGuard",
      pathname: currentPath,
      next: nextPath,
      hasUser: Boolean(authStore.state.user?.id),
      role: authStore.state.profile?.role ?? authStore.state.role ?? null,
      legalAcceptanceState: "unknown",
      action: "blocked_auto_next_missing_legal",
    });
    return;
  }

  if (expiredSessionDiagnostic) {
    if (authStore.expiredSessionRedirectInFlight) return;
    authStore.expiredSessionRedirectInFlight = true;
    logExpiredSessionRedirectOwner(expiredSessionDiagnostic);
  }
  window.location.replace(nextPath);
}

function readNearbyPublicCookie() {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .some((cookie) => cookie.trim() === "yb_nearby_public=1");
}

export function AuthProvider({
  children,
  initialUser = null,
  initialProfile = null,
  initialAccountContext = null,
  initialRole = null,
  initialAuthResolved = false,
  initialSupportModeActive = false,
}) {
  const parentAuth = useContext(AuthContext);
  const [isNestedProvider] = useState(() => Boolean(parentAuth?.providerInstanceId));
  const [initialSeedProviderToken] = useState(() => Symbol("auth-provider-seed"));
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isAuthHandoff = searchParams?.get(AUTH_HANDOFF_PARAM) === "1";
  const authDiagEnabledLocal = useMemo(
    () =>
      process.env.NEXT_PUBLIC_AUTH_DIAG === "1" &&
      process.env.NODE_ENV !== "production",
    []
  );
  const rscLoopDiagEnabled = useMemo(
    () =>
      process.env.NEXT_PUBLIC_RSC_LOOP_DIAG === "1" &&
      process.env.NODE_ENV !== "production",
    []
  );
  const allowBootstrap = !pathname?.startsWith("/business-auth");
  const serverAuthSnapshot = useMemo(
    () =>
      buildServerAuthSnapshot({
        initialUser,
        initialProfile,
        initialAccountContext,
        initialRole,
        initialAuthResolved,
        initialSupportModeActive,
      }),
    [
      initialAccountContext,
      initialAuthResolved,
      initialProfile,
      initialRole,
      initialSupportModeActive,
      initialUser,
    ]
  );
  const getServerAuthSnapshot = useCallback(
    () => serverAuthSnapshot,
    [serverAuthSnapshot]
  );
  if (typeof window !== "undefined") {
    primeAuthStateForInitialRender({
      initialUser,
      initialProfile,
      initialAccountContext,
      initialRole,
      initialAuthResolved,
      supportModeActive: Boolean(initialSupportModeActive),
      providerSeedToken: initialSeedProviderToken,
    });
  }
  const supabase = useMemo(
    () => (allowBootstrap ? getSupabaseBrowserClient() : null),
    [allowBootstrap]
  );
  const authState = useSyncExternalStore(
    subscribeAuthState,
    getAuthStateSnapshot,
    getServerAuthSnapshot
  );

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_AUTH_DIAG !== "1") return;
    console.info("[AUTH_CLIENT_BOOTSTRAP]", {
      source: "AuthProvider:first-snapshot",
      pathname,
      authStatus: authState.authStatus,
      authInitialized: authState.authInitialized,
      hasUser: Boolean(authState.user?.id),
      role: authState.role || null,
      hasInitialUser: Boolean(initialUser?.id),
      isAuthHandoff,
    });
  }, [
    authState.authInitialized,
    authState.accountContext?.hasBusinessAccess,
    authState.authStatus,
    authState.role,
    authState.user?.id,
    initialUser?.id,
    isAuthHandoff,
    pathname,
  ]);

  const mountedRef = useRef(true);
  const fetchWrappedRef = useRef(false);
  const redirectStateNormalizedRef = useRef(false);
  const rootSeedEnrichmentUserRef = useRef(null);
  const reactId = useId();
  const providerInstanceId = useMemo(
    () => `auth-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`,
    [reactId]
  );
  const authUiFailsafeTimerRef = useRef(null);
  const authRouteRedirectRef = useRef({
    userId: null,
    target: null,
    fromPath: null,
  });
  const router = useRouter();
  const lastKnownRoleRef = useRef(null);
  const logRefreshAttempt = useCallback(
    (reason) => {
      if (!rscLoopDiagEnabled || typeof window === "undefined") return;
      console.warn("[RSC_LOOP_DIAG] router.refresh_attempt", {
        reason,
        pathname: window.location.pathname,
        ts: Date.now(),
        stack: new Error().stack,
      });
    },
    [rscLoopDiagEnabled]
  );
  const triggerAutoRefreshBlocked = useCallback(
    (reason, blockedUntil) => {
      updateAuthState({
        refreshDisabledReason: reason,
        refreshDisabledUntil: blockedUntil,
      });
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(AUTO_REFRESH_BLOCKED_EVENT, {
            detail: { reason, blockedUntil, ts: Date.now() },
          })
        );
      }
      if (rscLoopDiagEnabled && typeof window !== "undefined") {
        console.warn("[RSC_LOOP_DIAG] auto_refresh_blocked", {
          reason,
          blockedUntil,
          pathname: window.location.pathname,
          stack: new Error().stack,
        });
      }
    },
    [rscLoopDiagEnabled]
  );
  const shouldAllowAutoRefresh = useCallback(
    (reason) => {
      const now = Date.now();
      const guardState = readRefreshGuardState();
      let attempts = guardState.attempts.filter(
        (ts) => now - ts < AUTO_REFRESH_WINDOW_MS
      );
      let blockedUntil = guardState.blockedUntil;
      let lastRefreshAt = guardState.lastRefreshAt;
      if (autoRefreshDisabled) {
        logAuthDiag("router:refresh:skipped", { reason, gate: "auto_refresh_disabled" });
        return false;
      }
      if (blockedUntil > now) {
        triggerAutoRefreshBlocked("rsc_loop_guard_cooldown", blockedUntil);
        return false;
      }
      if (now - lastRefreshAt < AUTO_REFRESH_MIN_INTERVAL_MS) {
        blockedUntil = now + AUTO_REFRESH_COOLDOWN_MS;
        writeRefreshGuardState({
          attempts: [],
          blockedUntil,
          lastRefreshAt,
        });
        triggerAutoRefreshBlocked("rsc_loop_guard_min_interval", blockedUntil);
        return false;
      }
      if (attempts.length >= AUTO_REFRESH_MAX_ATTEMPTS) {
        blockedUntil = now + AUTO_REFRESH_COOLDOWN_MS;
        writeRefreshGuardState({
          attempts: [],
          blockedUntil,
          lastRefreshAt,
        });
        triggerAutoRefreshBlocked("rsc_loop_guard_rate_limit", blockedUntil);
        return false;
      }
      attempts.push(now);
      lastRefreshAt = now;
      writeRefreshGuardState({
        attempts,
        blockedUntil: 0,
        lastRefreshAt,
      });
      if (authStore.state.refreshDisabledReason || authStore.state.refreshDisabledUntil) {
        updateAuthState({
          refreshDisabledReason: null,
          refreshDisabledUntil: 0,
        });
      }
      return true;
    },
    [triggerAutoRefreshBlocked]
  );
  const guardedRouterRefresh = useCallback(
    (reason) => {
      if (!shouldAllowAutoRefresh(reason)) return false;
      logRefreshAttempt(reason);
      router.refresh();
      return true;
    },
    [logRefreshAttempt, router, shouldAllowAutoRefresh]
  );

  useEffect(() => {
    if (authState.role) {
      lastKnownRoleRef.current = authState.role;
    }
  }, [authState.role]);

  useEffect(() => {
    if (isNestedProvider) return;
    if (isAuthNavigationSuppressed()) return;
    if (!authState.authInitialized) return;
    if (authState.lastAuthEvent !== "SIGNED_OUT") return;
    if (
      authState.authReconciliationStatus === "in_progress" ||
      authState.authReconciliationStatus === "uncertain" ||
      authState.serverAuthAuthority !== "confirmed_guest"
    ) {
      return;
    }
    if (isPwaDeploymentRecoveryActive() && !authStore.loggingOut) {
      recordPwaSessionExpirySuppressed();
      return;
    }
    if (!pathname) return;
    const currentPath = typeof window !== "undefined" ? window.location.pathname : pathname;
    if (isAuthCallbackPath(pathname) || isAuthCallbackPath(currentPath)) return;
    if (isBootstrapOnboardingPath(currentPath)) {
      logCustomerRouteLoopDiag({
        source: "AuthProvider.signed_out_event_guard",
        pathname: currentPath,
        normalizedPathname: currentPath,
        searchParamKeys: getCurrentSearchParamKeys(),
        hasUser: Boolean(authState.user?.id),
        role: authState.profile?.role ?? authState.role ?? null,
        redirectDecision: "allow_onboarding_hydration",
        redirectDestination: null,
        redirectType: null,
      });
      if (process.env.NODE_ENV !== "production") {
        console.info("[ONBOARDING_REDIRECT_TRACE] source=AuthProvider_guard_allow", {
          pathname: currentPath,
          reason: "signed_out_event_guard",
          role: authState.profile?.role ?? authState.role ?? null,
          userId: authState.user?.id ?? null,
        });
      }
      return;
    }
    if (!isProtectedPath(pathname)) return;
    if (isCustomerNearbyPublicAllowed(pathname, readNearbyPublicCookie())) return;
    const target = getSessionRecoveryLoginPath(pathname);
    if (pathname === target || pathname === `${target}/`) return;
    const normalizedTarget = normalizeNextPath(target) || target;
    if (process.env.NODE_ENV !== "production" && normalizedTarget !== target) {
      console.info("[ONBOARDING_REDIRECT_TRACE] source=normalizeNextPath_rewrite", {
        from: target,
        to: normalizedTarget,
      });
    }
    logAuthDiag("route_guard:signed_out_redirect", { from: pathname, to: normalizedTarget });
    if (process.env.NODE_ENV !== "production") {
      console.info("[ONBOARDING_REDIRECT_TRACE] source=AuthProvider_guard_redirect", {
        from: currentPath,
        to: normalizedTarget,
        role: authState.profile?.role ?? authState.role ?? null,
        userId: authState.user?.id ?? null,
        reason: "signed_out_protected_route",
      });
    }
    redirectWithGuard(normalizedTarget, {
      owner: "auth_provider_signed_out",
      trigger: "signed_out",
    });
  }, [
    authState.authInitialized,
    authState.authReconciliationStatus,
    authState.lastAuthEvent,
    authState.profile?.role,
    authState.role,
    authState.serverAuthAuthority,
    authState.user?.id,
    isNestedProvider,
    pathname,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    authStore.providerCount += 1;
    authStore.providerInstanceId = providerInstanceId;
    if (authDiagEnabledLocal && authStore.providerCount > 1) {
      console.warn("[AUTH_DIAG] provider:multiple", {
        providerInstanceId,
        providerCount: authStore.providerCount,
        pathname: typeof window !== "undefined" ? window.location.pathname : null,
      });
    }

    if (supabase && !authStore.supabase) {
      authStore.supabase = supabase;
    }

    seedAuthState({
      initialUser,
      initialProfile,
      initialAccountContext,
      initialRole,
      supportModeActive: Boolean(initialSupportModeActive),
    });

    if (!isNestedProvider && !redirectStateNormalizedRef.current) {
      redirectStateNormalizedRef.current = true;
      normalizeClientRedirectStateInPlace();
    }

    if (!isNestedProvider && allowBootstrap) {
      ensureAuthGuardSubscription();
      ensureAuthListener();
      ensureAuthResumeLifecycle();

      if (!authStore.state.user && !authStore.state.supportModeActive) {
        void bootstrapAuth({ trigger: "initial_hydration" });
      }
    }

    return () => {
      mountedRef.current = false;
      authStore.providerCount = Math.max(0, authStore.providerCount - 1);
      if (authStore.providerCount === 0) {
        releaseAuthListener();
        releaseAuthResumeLifecycle();
      }
    };
  }, [
    initialProfile,
    initialAccountContext,
    initialRole,
    initialSupportModeActive,
    initialUser,
    isNestedProvider,
    supabase,
    authDiagEnabledLocal,
    allowBootstrap,
    providerInstanceId,
  ]);

  useEffect(() => {
    const userId = initialUser?.id || null;
    if (
      isNestedProvider ||
      !allowBootstrap ||
      !userId ||
      initialProfile ||
      initialAccountContext ||
      authStore.state.supportModeActive ||
      authStore.state.user?.id !== userId ||
      rootSeedEnrichmentUserRef.current === userId
    ) {
      return;
    }
    rootSeedEnrichmentUserRef.current = userId;
    // Identity is already authoritative and usable. Enrich profile/access in
    // the background without moving auth back to an initializing state.
    void applyUserUpdate(authStore.state.user);
  }, [
    allowBootstrap,
    initialAccountContext,
    initialProfile,
    initialUser,
    isNestedProvider,
  ]);

  useEffect(() => {
    if (!authDiagEnabledLocal) return undefined;
    authClickTracerRefs += 1;
    if (authClickTracerRefs === 1) {
      authClickTracerCleanup = attachAuthClickTracer();
    }
    return () => {
      authClickTracerRefs = Math.max(0, authClickTracerRefs - 1);
      if (authClickTracerRefs === 0 && authClickTracerCleanup) {
        authClickTracerCleanup();
        authClickTracerCleanup = null;
      }
    };
  }, [authDiagEnabledLocal]);

  useLayoutEffect(() => {
    if (isNestedProvider) return;
    if (!pathname) return;
    emitAuthUiReset("route_change");
  }, [isNestedProvider, pathname]);

  useEffect(() => {
    if (!isAuthHandoff) return;
    if (!authState.authInitialized) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname === "/customer/onboarding") return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has(AUTH_HANDOFF_PARAM)) return;
    url.searchParams.delete(AUTH_HANDOFF_PARAM);
    url.searchParams.delete(AUTH_FRESH_PARAM);
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, "", nextUrl || url.pathname);
  }, [authState.authInitialized, isAuthHandoff]);

  useEffect(() => {
    if (isNestedProvider) return;
    if (!pathname) return;
    if (!authStore.state.supportModeActive) return;
    if (pathname.startsWith("/customer")) return;
    updateAuthState({ supportModeActive: false });
    void bootstrapAuth({ trigger: "support_mode_exit" });
  }, [isNestedProvider, pathname]);

  useEffect(() => {
    if (isNestedProvider) return;
    if (typeof window === "undefined") return;
    const signedOut = searchParams?.get("signedout") === "1";
    if (!signedOut) return;

    clearVerifiedUserCache();
    clearSupabaseAuthStorage();
    clearSupabaseCookiesClient();
    applySignedOutState("signedout_param", {
      resetGuardState: true,
      clearAuthSuppression: true,
    });
    resetSupabaseClient();

    const params = new URLSearchParams(searchParams.toString());
    params.delete("signedout");
    const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
    window.history.replaceState({}, "", next);
    if (process.env.NODE_ENV !== "production") {
      console.info("[AUTH_REDIRECT_TRACE] signedout_param_cleared", {
        pathname: window.location.pathname,
      });
    }
  }, [isNestedProvider, searchParams]);

  useEffect(() => {
    if (isNestedProvider) return;
    const now = Date.now();
    const state = readRefreshGuardState();
    if (state.blockedUntil > now) {
      updateAuthState({
        refreshDisabledReason: "rsc_loop_guard_cooldown",
        refreshDisabledUntil: state.blockedUntil,
      });
    }
  }, [isNestedProvider]);

  useEffect(() => {
    if (!rscLoopDiagEnabled || typeof window === "undefined") return undefined;
    const handleRetry = () => {
      writeRefreshGuardState({
        attempts: [],
        blockedUntil: 0,
        lastRefreshAt: 0,
      });
      updateAuthState({
        refreshDisabledReason: null,
        refreshDisabledUntil: 0,
      });
      guardedRouterRefresh("manual_retry");
    };
    window.addEventListener(AUTO_REFRESH_RETRY_EVENT, handleRetry);
    window.__YB_DIAG_TRIGGER_REFRESH = (reason = "manual_diag") => {
      guardedRouterRefresh(String(reason || "manual_diag"));
    };
    window.__YB_DIAG_SIMULATE_AUTH_EVENT = (event = "TOKEN_REFRESHED") => {
      updateAuthState({ lastAuthEvent: String(event) });
    };
    return () => {
      window.removeEventListener(AUTO_REFRESH_RETRY_EVENT, handleRetry);
      delete window.__YB_DIAG_TRIGGER_REFRESH;
      delete window.__YB_DIAG_SIMULATE_AUTH_EVENT;
    };
  }, [guardedRouterRefresh, rscLoopDiagEnabled]);

  useEffect(() => {
    if (isNestedProvider) return undefined;
    if (process.env.NODE_ENV === "production") return undefined;
    if (authState.authStatus !== "loading") return undefined;
    const timer = setTimeout(() => {
      if (authStore.state.authStatus === "loading") {
        console.warn(
          "[AUTH_DIAG] authStatus still loading after 2s",
          authStore.state
        );
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [authState.authStatus, isNestedProvider]);

  useEffect(() => {
    if (isNestedProvider) return;
    if (!authState.tokenInvalidAt) return;
    if (authState.tokenInvalidAt <= handledTokenInvalidAt) return;
    handledTokenInvalidAt = authState.tokenInvalidAt;

    (async () => {
      logAuthDiag("auth:token_invalid:handle", {
        tokenInvalidAt: authState.tokenInvalidAt,
      });
      if (authStore.loggingOut || authStore.logoutRedirectInFlight) {
        acknowledgeAuthTokenInvalid(authState.tokenInvalidAt);
        return;
      }
      const reconciliation = await reconcileClientAuthWithServer("token_invalid");
      if (reconciliation.result === "authenticated") {
        acknowledgeAuthTokenInvalid(authState.tokenInvalidAt);
        updateAuthState({ tokenInvalidAt: 0 });
        return;
      }
      if (
        reconciliation.result === "uncertain" ||
        reconciliation.result === "aborted"
      ) {
        acknowledgeAuthTokenInvalid(authState.tokenInvalidAt);
        updateAuthState({ tokenInvalidAt: 0 });
        return;
      }
      await rejectTerminalSession("token_invalid");
      applySignedOutState("token_invalid", {
        resetGuardState: true,
        clearAuthSuppression: true,
        extraState: {
          serverAuthAuthority: "confirmed_guest",
        },
      });
      acknowledgeAuthTokenInvalid(authState.tokenInvalidAt);
      logAuthReconciliation({
        attemptId: reconciliation.attemptId,
        trigger: "token_invalid",
        result: "confirmed_guest",
        generation: reconciliation.generation,
        terminalCleanupProceeded: true,
      });

      const currentPath = typeof window !== "undefined" ? window.location.pathname : pathname;
      const target = getSessionRecoveryLoginPath(currentPath, {
        business: authStore.state.role === "business",
      });
      const normalizedTarget = normalizeNextPath(target) || target;
      if (isBootstrapOnboardingPath(currentPath)) {
        if (process.env.NODE_ENV !== "production") {
          console.info("[ONBOARDING_REDIRECT_TRACE] source=AuthProvider_guard_allow", {
            pathname: currentPath,
            reason: "token_invalid_guard",
            role: authStore.state.profile?.role ?? authStore.state.role ?? null,
            userId: authStore.state.user?.id ?? null,
          });
        }
        return;
      }
      if (process.env.NODE_ENV !== "production" && normalizedTarget !== target) {
        console.info("[ONBOARDING_REDIRECT_TRACE] source=normalizeNextPath_rewrite", {
          from: target,
          to: normalizedTarget,
        });
      }
      if (process.env.NODE_ENV !== "production") {
        console.info("[ONBOARDING_REDIRECT_TRACE] source=AuthProvider_guard_redirect", {
          from: currentPath,
          to: normalizedTarget,
          role: authStore.state.role ?? null,
          userId: authStore.state.user?.id ?? null,
          reason: "token_invalid",
        });
      }
      redirectWithGuard(normalizedTarget, {
        owner: "auth_provider_token_invalid",
        trigger: "token_invalid",
        tokenInvalid: true,
      });
    })();
  }, [authState.tokenInvalidAt, isNestedProvider, pathname]);

  useEffect(() => {
    if (isNestedProvider) return;
    if (!authState.authInitialized) return;
    if (!authState.user?.id) return;
    const status = normalizeAccountStatus(authState.profile?.account_status);
    if (!isBlockedAccountStatus(status)) return;

    (async () => {
      await rejectTerminalSession("account_lifecycle_blocked");
      applySignedOutState("account_lifecycle_effect_block", {
        resetGuardState: true,
        clearAuthSuppression: true,
        extraState: {
          lastAuthEvent: "SIGNED_OUT",
          lastError: "This account is no longer available.",
        },
      });
      const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
      if (!isPublicLoginSurfacePath(currentPath) && !shouldSuppressAuthUiReset()) {
        redirectWithGuard(getAccountDeletedRedirectPath());
      }
    })();
  }, [
    authState.authInitialized,
    authState.profile?.account_status,
    authState.user?.id,
    isNestedProvider,
  ]);

  const refreshProfile = useCallback(async () => {
    if (!mountedRef.current || !authStore.supabase || !authState.user?.id) {
      return;
    }
    const { profile, accountContext, error } = await fetchProfile(authState.user);
    if (error || !mountedRef.current) return;
    const currentState = authStore.state;
    if (!currentState.user?.id || currentState.user.id !== authState.user.id) {
      return;
    }
    const nextProfile = mergeFreshProfile(currentState.profile, profile, currentState.user);
    const nextRole = resolveRole(nextProfile, currentState.user, currentState.role);
    let business = null;
    if (nextRole === "business") {
      const { business: businessRow } = await fetchBusiness(currentState.user);
      if (!mountedRef.current) return;
      const latestState = authStore.state;
      if (latestState.user?.id !== currentState.user.id) return;
      business = mergeBusinessEnrichmentState(
        latestState.business,
        businessRow,
        latestState.user,
        latestState.businessContextStatus
      );
      authStore.businessUserId = currentState.user.id;
      authStore.businessPromise = null;
    } else {
      authStore.businessPromise = null;
      authStore.businessUserId = null;
    }
    updateAuthState({
      profile: nextProfile,
      business,
      accountContext: accountContext ?? currentState.accountContext,
      role: nextRole,
    });
  }, [authState.user]);

  const updateProfile = useCallback((nextProfile) => {
    const currentState = authStore.state;
    if (!nextProfile?.id || nextProfile.id !== currentState.user?.id) {
      return;
    }
    const mergedProfile = mergeFreshProfile(
      currentState.profile,
      nextProfile,
      currentState.user
    );
    const mergedBusiness =
      currentState.business?.owner_user_id === currentState.user?.id ||
      currentState.business?.id === currentState.user?.id
        ? {
            ...currentState.business,
            ...nextProfile,
            id: currentState.business?.id ?? nextProfile.id,
            owner_user_id: currentState.business?.owner_user_id ?? nextProfile.id,
          }
        : currentState.business;
    const nextRole = resolveRole(mergedProfile, currentState.user, currentState.role);
    updateAuthState({
      profile: mergedProfile,
      business: mergedBusiness,
      role: nextRole,
    });
  }, []);

  const resetAuthUiStateCb = useCallback((reason) => {
    resetAuthUiState(reason);
  }, []);

  const retryAuth = useCallback(async () => {
    if (authStore.bootstrapPromise) return authStore.bootstrapPromise;
    if (authStore.bootstrapRetryTimer) {
      window.clearTimeout(authStore.bootstrapRetryTimer);
      authStore.bootstrapRetryTimer = null;
    }
    authStore.bootstrapWaitingForOnline = false;
    return bootstrapAuth({ trigger: "manual_retry", transientRetryAttempt: 0 });
  }, []);

  const retryAuthResume = useCallback(async () => {
    if (authStore.resumeRetryTimer) {
      window.clearTimeout(authStore.resumeRetryTimer);
      authStore.resumeRetryTimer = null;
    }
    return requestAuthResume("manual_retry", { transientRetryAttempt: 0 });
  }, []);

  const seedAuthStateCb = useCallback((payload) => {
    seedAuthState(payload);
  }, []);

  const logout = useCallback(
    async (options = {}) => {
      if (isAuthNavigationSuppressed()) return;
      const { redirectTo, reason = "logout" } = options;
      const role = authStore.state.role;
      const inferredRole =
        role ||
        (typeof window !== "undefined" &&
        window.location.pathname.startsWith("/business")
          ? "business"
          : "customer");
      const publicRedirect = resolveLogoutRedirect({
        role: inferredRole,
        redirectTo,
      });

      resetAuthUiState("logout:pre");
      emitAuthUiReset("logout:pre");
      authStore.sessionGeneration += 1;
      authStore.reconciliationCooldownUntil = 0;
      authStore.reconciliationLastResult = null;
      authStore.loggingOut = true;
      authStore.logoutRedirectInFlight = true;
      applySignedOutState("logout:pre", {
        resetGuardState: true,
        resetAuthUi: false,
        extraState: {
          lastAuthEvent: "SIGNED_OUT",
          lastError: null,
        },
      });

      logAuthDiag("logout", {
        hasUser: Boolean(authStore.state.user),
        authStatus: authStore.state.authStatus,
        authBusy: authStore.state.authBusy,
        reason,
        role,
        redirectTo: publicRedirect,
      });

      clearVerifiedUserCache();
      clearSupabaseAuthStorage();
      clearSupabaseCookiesClient();
      await performLogout({
        supabase: authStore.supabase,
        role: inferredRole,
        redirectTo: publicRedirect,
        callServerSignout: typeof window !== "undefined",
      });
    },
    []
  );

  const value = useMemo(
    () => ({
      supabase: authStore.supabase ?? supabase,
      authStatus: authState.authStatus,
      authReadiness: authState.authReadiness,
      authReconciliationStatus: authState.authReconciliationStatus,
      serverAuthAuthority: authState.serverAuthAuthority,
      explicitLogoutActive: isAuthNavigationSuppressed(),
      terminalInvalidSessionActive: authStore.terminalSessionRejected,
      tokenInvalidSessionActive: Boolean(authState.tokenInvalidAt),
      authorityRevision: authState.authorityRevision,
      authResumeStatus: authState.authResumeStatus,
      authResumeRevision: authState.authResumeRevision,
      authResumeError: authState.authResumeError,
      businessContextStatus: authState.businessContextStatus,
      authInitialized: authState.authInitialized,
      session: authState.session,
      status:
        authState.authReadiness === "recoverable_error"
          ? "recoverable_error"
          : authState.authStatus === "authenticated"
          ? "signed_in"
          : authState.authStatus === "unauthenticated"
            ? "signed_out"
            : "loading",
      user: authState.user,
      profile: authState.profile,
      business: authState.business,
      accountContext: authState.accountContext,
      hasBusinessAccess: authState.accountContext?.hasBusinessAccess === true,
      businessAccessContexts: authState.accountContext?.businessAccessContexts || [],
      role: authState.role,
      supportModeActive: authState.supportModeActive === true,
      error: authState.error,
      lastAuthEvent: authState.lastAuthEvent,
      lastError: authState.lastError,
      loadingUser:
        authState.authStatus === "loading",
      rateLimited: authState.rateLimited,
      rateLimitUntil: authState.rateLimitUntil,
      rateLimitMessage: authState.rateLimitMessage,
      refreshDisabledUntil: authState.refreshDisabledUntil,
      refreshDisabledReason: authState.refreshDisabledReason,
      authBusy: authState.authBusy,
      authAction: authState.authAction,
      authAttemptId: authState.authAttemptId,
      authActionStartedAt: authState.authActionStartedAt,
      providerInstanceId,
      refreshProfile,
      refreshBusinessProfile: refreshProfile,
      updateProfile,
      logout,
      beginAuthAttempt,
      endAuthAttempt,
      resetAuthUiState: resetAuthUiStateCb,
      retryAuth,
      retryAuthResume,
      seedAuthState: seedAuthStateCb,
    }),
    [
      authState.authStatus,
      authState.authReadiness,
      authState.authReconciliationStatus,
      authState.serverAuthAuthority,
      authState.authorityRevision,
      authState.authResumeStatus,
      authState.authResumeRevision,
      authState.authResumeError,
      authState.businessContextStatus,
      authState.tokenInvalidAt,
      authState.authInitialized,
      authState.business,
      authState.accountContext,
      authState.session,
      authState.error,
      authState.profile,
      authState.rateLimitMessage,
      authState.rateLimitUntil,
      authState.rateLimited,
      authState.refreshDisabledReason,
      authState.refreshDisabledUntil,
      authState.role,
      authState.supportModeActive,
      authState.user,
      authState.lastAuthEvent,
      authState.lastError,
      authState.authBusy,
      authState.authAction,
      authState.authAttemptId,
      authState.authActionStartedAt,
      supabase,
      logout,
      refreshProfile,
      updateProfile,
      resetAuthUiStateCb,
      retryAuth,
      retryAuthResume,
      seedAuthStateCb,
      providerInstanceId,
    ]
  );

  useEffect(() => {
    if (isNestedProvider) return;
    if (!authDiagEnabledLocal) return;
    console.log("[AUTH_DIAG] status", {
      providerInstanceId,
      authStatus: authState.authStatus,
      status:
        authState.authStatus === "authenticated"
          ? "signed_in"
          : authState.authStatus === "unauthenticated"
            ? "signed_out"
            : "loading",
      hasUser: Boolean(authState.user),
      hasProfile: Boolean(authState.profile),
      lastAuthEvent: authState.lastAuthEvent,
      lastError: authState.lastError,
      authBusy: authState.authBusy,
      authAction: authState.authAction,
      authAttemptId: authState.authAttemptId,
      authActionStartedAt: authState.authActionStartedAt,
    });
  }, [
    authState.authStatus,
    authState.authAction,
    authState.authAttemptId,
    authState.authBusy,
    authState.authActionStartedAt,
    authState.lastAuthEvent,
    authState.lastError,
    authState.profile,
    authState.user,
    authDiagEnabledLocal,
    isNestedProvider,
    providerInstanceId,
  ]);

  useEffect(() => {
    if (isNestedProvider) return;
    if (!authDiagEnabledLocal || fetchWrappedRef.current) return;
    if (typeof window === "undefined" || typeof window.fetch !== "function") return;
    fetchWrappedRef.current = true;
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const input = args[0];
      const init = args[1] || {};
      const url = typeof input === "string" ? input : input?.url || "";
      const requestHeaders =
        input instanceof Request
          ? input.headers
          : new Headers(init?.headers || {});
      const hasRscHeader = requestHeaders.has("RSC") || requestHeaders.has("rsc");
      const isRedirectParam = url.includes("redirected=1");
      const response = await originalFetch(...args);

      if (
        (hasRscHeader || isRedirectParam) &&
        (response.redirected || response.status >= 300)
      ) {
        console.warn("[AUTH_DIAG] fetch:rsc", {
          url,
          status: response.status,
          redirected: response.redirected,
          location: response.headers.get("location"),
          authStatus: authStore.state.authStatus,
          stack: new Error().stack,
        });
      }

      if (response.status === 401 || response.status === 403) {
        console.warn("[AUTH_DIAG] fetch:unauthorized", {
          url,
          status: response.status,
          authStatus: authStore.state.authStatus,
          stack: new Error().stack,
        });
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
      fetchWrappedRef.current = false;
    };
  }, [authDiagEnabledLocal, isNestedProvider]);

  useEffect(() => {
    if (isNestedProvider) return;
    if (authState.authStatus !== "unauthenticated" || authState.user) return;
    if (isPwaDeploymentRecoveryActive() && !authStore.loggingOut) {
      recordPwaSessionExpirySuppressed();
      return;
    }
    if (authStore.logoutRedirectInFlight) return;
    if (authStore.loggingOut) {
      authStore.loggingOut = false;
    }
  }, [authState.authStatus, authState.user, isNestedProvider]);

  useEffect(() => {
    if (isNestedProvider) return;
    if (typeof window === "undefined") return;
    if (isAuthNavigationSuppressed()) return;
    if (!authState.authInitialized) return;
    if (authState.authReadiness !== "ready") return;
    if (authState.authStatus !== "unauthenticated" || authState.user) return;
    if (
      authState.authReconciliationStatus === "in_progress" ||
      authState.authReconciliationStatus === "uncertain"
    ) {
      return;
    }
    if (!pathname) return;
    const currentPath = typeof window !== "undefined" ? window.location.pathname : pathname;
    if (process.env.NODE_ENV !== "production") {
      console.info("[auth-next] client session effect:", {
        effect: "unauthenticated_guard",
        pathname: currentPath,
        authStatus: authState.authStatus,
        hasUser: Boolean(authState.user),
      });
    }
    if (isAuthCallbackPath(pathname) || isAuthCallbackPath(currentPath)) return;
    if (isBootstrapOnboardingPath(currentPath)) {
      logCustomerRouteLoopDiag({
        source: "AuthProvider.unauthenticated_guard",
        pathname: currentPath,
        normalizedPathname: currentPath,
        searchParamKeys: getCurrentSearchParamKeys(),
        hasUser: Boolean(authState.user),
        role: authState.profile?.role ?? authState.role ?? null,
        redirectDecision: "allow_onboarding_hydration",
        redirectDestination: null,
        redirectType: null,
      });
      if (process.env.NODE_ENV !== "production") {
        console.info("[ONBOARDING_REDIRECT_TRACE] source=AuthProvider_guard_allow", {
          pathname: currentPath,
          reason: "unauthenticated_guard",
          role: authState.profile?.role ?? authState.role ?? null,
          userId: authState.user?.id ?? null,
        });
      }
      return;
    }
    if (!isProtectedPath(pathname)) return;
    if (isCustomerNearbyPublicAllowed(pathname, readNearbyPublicCookie())) return;
    if (pathname.startsWith("/business-auth")) return;
    if (isPwaDeploymentRecoveryActive() && !authStore.loggingOut) {
      recordPwaSessionExpirySuppressed();
      return;
    }
    if (authState.serverAuthAuthority !== "confirmed_guest") {
      void reconcileClientAuthWithServer("route_guard");
      return;
    }

    const target = pathname.startsWith("/business/")
      ? getSessionRecoveryLoginPath(pathname)
      : pathname.startsWith("/business")
        ? PATHS.public.businessLanding
        : getSessionRecoveryLoginPath(pathname);
    if (pathname === target || pathname === `${target}/`) return;
    const normalizedTarget = normalizeNextPath(target) || target;
    if (process.env.NODE_ENV !== "production" && normalizedTarget !== target) {
      console.info("[ONBOARDING_REDIRECT_TRACE] source=normalizeNextPath_rewrite", {
        from: target,
        to: normalizedTarget,
      });
    }

    logAuthDiag("route_guard:client_redirect", {
      from: pathname,
      to: normalizedTarget,
      role: lastKnownRoleRef.current,
    });
    logBusinessDashboardRedirectDiag({
      source: "AuthProvider.unauthenticated_guard",
      action: "redirect_unauthenticated_protected_route",
      pathname: currentPath,
      authStatus: authState.authStatus,
      hasUser: Boolean(authState.user),
      role: authState.profile?.role ?? authState.role ?? null,
      hasBusinessAccess: authState.accountContext?.hasBusinessAccess === true,
      redirectDestination: normalizedTarget,
    });
    logCustomerRouteLoopDiag({
      source: "AuthProvider.unauthenticated_guard",
      pathname: currentPath,
      normalizedPathname: currentPath,
      searchParamKeys: getCurrentSearchParamKeys(),
      hasUser: Boolean(authState.user),
      role: authState.profile?.role ?? authState.role ?? null,
      redirectDecision: "redirect_unauthenticated_protected_route",
      redirectDestination: normalizedTarget,
      redirectType: "client_window_replace",
    });
    if (process.env.NODE_ENV !== "production") {
      console.info("[AUTH_REDIRECT_TRACE] unauthenticated_redirect", {
        pathname,
        role: lastKnownRoleRef.current ?? null,
        hasSession: Boolean(authState.session),
        hasUser: Boolean(authState.user),
        destination: normalizedTarget,
      });
      console.info("[auth-next] router replace:", normalizedTarget);
      console.info("[ONBOARDING_REDIRECT_TRACE] source=AuthProvider_guard_redirect", {
        from: currentPath,
        to: normalizedTarget,
        role: authState.profile?.role ?? authState.role ?? null,
        userId: authState.user?.id ?? null,
        reason: "unauthenticated_protected_route",
      });
    }
    redirectWithGuard(normalizedTarget, {
      owner: "auth_provider_route_guard",
      trigger: "protected_route_confirmed_guest",
    });
  }, [
    authState.authInitialized,
    authState.authReadiness,
    authState.authReconciliationStatus,
    authState.accountContext?.hasBusinessAccess,
    authState.authStatus,
    authState.profile?.role,
    authState.role,
    authState.serverAuthAuthority,
    authState.session,
    authState.user,
    authState.user?.id,
    isNestedProvider,
    pathname,
  ]);

  useEffect(() => {
    if (isNestedProvider) return;
    if (typeof window === "undefined") return;
    if (isAuthNavigationSuppressed()) return;
    if (authState.authStatus !== "authenticated" || !authState.user) return;
    if (!pathname) return;
    const currentPath = typeof window !== "undefined" ? window.location.pathname : pathname;
    if (process.env.NODE_ENV !== "production") {
      console.info("[auth-next] client session effect:", {
        effect: "authenticated_guard",
        pathname: currentPath,
        authStatus: authState.authStatus,
        hasUser: Boolean(authState.user),
        role: authState.profile?.role ?? authState.role ?? null,
      });
    }
    if (isBootstrapOnboardingPath(currentPath)) {
      logCustomerRouteLoopDiag({
        source: "AuthProvider.authenticated_guard",
        pathname: currentPath,
        normalizedPathname: currentPath,
        searchParamKeys: getCurrentSearchParamKeys(),
        hasUser: Boolean(authState.user),
        role: authState.profile?.role ?? authState.role ?? null,
        redirectDecision: "allow_onboarding_authenticated",
        redirectDestination: null,
        redirectType: null,
      });
      if (process.env.NODE_ENV !== "production") {
        console.info("[ONBOARDING_REDIRECT_TRACE] source=AuthProvider_guard_allow", {
          pathname: currentPath,
          reason: "authenticated_business_auth_redirect_guard",
          role: authState.profile?.role ?? authState.role ?? null,
          userId: authState.user?.id ?? null,
        });
      }
      return;
    }
    if (
      isBusinessCreatePasswordPath(pathname) ||
      isBusinessCreatePasswordPath(currentPath) ||
      isBusinessPostConfirmPath(pathname) ||
      isBusinessPostConfirmPath(currentPath)
    ) {
      console.info("[BUSINESS_REDIRECT_TRACE] auth_provider_create_password_allow", {
        pathname: currentPath,
        userId: authState.user?.id ?? null,
        role: authState.profile?.role ?? authState.role ?? null,
        sessionExists: Boolean(authState.session),
        password_set: null,
        onboardingState: null,
        redirectDestination: null,
        redirectReason: "allow_authenticated_business_setup_route",
      });
      return;
    }
    if (!pathname.startsWith("/business-auth")) return;
    const resolvedRole = authState.profile?.role ?? authState.role;
    if (!resolvedRole) {
      if (process.env.NODE_ENV !== "production") {
        console.info("[AUTH_REDIRECT_TRACE] auth_redirect_waiting_for_role", {
          pathname,
          hasSession: Boolean(authState.session),
          hasUser: Boolean(authState.user),
          role: null,
        });
      }
      return;
    }

    const requestedPath =
      searchParams?.get("next") ||
      searchParams?.get("returnUrl") ||
      searchParams?.get("callbackUrl") ||
      null;
    const normalizedRequestedPath = normalizeNextPath(requestedPath) || requestedPath;
    if (
      process.env.NODE_ENV !== "production" &&
      normalizedRequestedPath &&
      normalizedRequestedPath !== requestedPath
    ) {
      console.info("[ONBOARDING_REDIRECT_TRACE] source=normalizeNextPath_rewrite", {
        from: requestedPath,
        to: normalizedRequestedPath,
      });
    }
    const target = getPostLoginRedirect({
      role: resolvedRole,
      requestedPath: normalizedRequestedPath,
      fallbackPath:
        resolvedRole === "business" ? PATHS.business.dashboard : PATHS.customer.home,
    });
    const normalizedTarget = normalizeNextPath(target) || target;
    if (process.env.NODE_ENV !== "production" && normalizedTarget !== target) {
      console.info("[ONBOARDING_REDIRECT_TRACE] source=normalizeNextPath_rewrite", {
        from: target,
        to: normalizedTarget,
      });
    }
    const redirectState = authRouteRedirectRef.current;
    const sameRedirect =
      redirectState.userId === authState.user.id &&
      redirectState.target === normalizedTarget &&
      redirectState.fromPath === pathname;
    if (sameRedirect) return;
    if (pathname === normalizedTarget || pathname === `${normalizedTarget}/`) return;

    logAuthDiag("route_guard:auth_redirect", {
      from: pathname,
      to: normalizedTarget,
      role: authState.role,
    });
    if (process.env.NODE_ENV !== "production") {
      console.info("[AUTH_REDIRECT_TRACE] auth_provider_redirect_effect", {
        role: authState.profile?.role ?? authState.role ?? null,
        requestedPath,
        chosenDestination: normalizedTarget,
        persistedRedirectState: readClientRedirectState(),
        hasSession: Boolean(authState.session),
      });
      console.info("[auth-next] router replace:", normalizedTarget);
      console.info("[ONBOARDING_REDIRECT_TRACE] source=AuthProvider_guard_redirect", {
        from: currentPath,
        to: normalizedTarget,
        role: authState.profile?.role ?? authState.role ?? null,
        userId: authState.user?.id ?? null,
        reason: "authenticated_business_auth_route",
      });
    }
    authRouteRedirectRef.current = {
      userId: authState.user.id,
      target: normalizedTarget,
      fromPath: pathname,
    };
    if (process.env.NODE_ENV !== "production") {
      console.info("[ONBOARDING_REDIRECT_TRACE] source=AuthProvider_guard_redirect", {
        from: currentPath,
        to: normalizedTarget,
        role: authState.profile?.role ?? authState.role ?? null,
        userId: authState.user?.id ?? null,
        reason: "authenticated_business_auth_route_router_replace",
      });
    }
    router.replace(normalizedTarget);
  }, [
    authState.authStatus,
    authState.profile?.is_internal,
    authState.profile?.role,
    authState.role,
    authState.session,
    authState.user,
    isNestedProvider,
    pathname,
    searchParams,
    router,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (process.env.NODE_ENV === "production") return;
    console.info("[auth-next] final mounted pathname:", window.location.pathname + window.location.search);
  }, [pathname, searchParams]);

  useEffect(() => {
    if (isNestedProvider) return;
    if (authState.authStatus !== "unauthenticated" || authState.user) {
      if (authUiFailsafeTimerRef.current) {
        clearTimeout(authUiFailsafeTimerRef.current);
        authUiFailsafeTimerRef.current = null;
      }
      return;
    }
    if (!authState.authBusy && !authState.authAction) {
      if (authUiFailsafeTimerRef.current) {
        clearTimeout(authUiFailsafeTimerRef.current);
        authUiFailsafeTimerRef.current = null;
      }
      return;
    }

    const startedAt = authState.authActionStartedAt || 0;
    const ageMs = startedAt ? Date.now() - startedAt : 0;
    const remainingMs = startedAt ? Math.max(0, 5000 - ageMs) : 0;

    if (remainingMs === 0) {
      logAuthDiag("auth_ui:failsafe", {
        ageMs,
        authAction: authState.authAction,
        authAttemptId: authState.authAttemptId,
      });
      updateAuthState({
        authBusy: false,
        authAction: null,
        authActionStartedAt: 0,
      });
      return;
    }

    if (authUiFailsafeTimerRef.current) {
      clearTimeout(authUiFailsafeTimerRef.current);
    }
    authUiFailsafeTimerRef.current = setTimeout(() => {
      authUiFailsafeTimerRef.current = null;
      if (authStore.state.authStatus !== "unauthenticated") return;
      if (authStore.state.user) return;
      logAuthDiag("auth_ui:failsafe", {
        ageMs: Date.now() - startedAt,
        authAction: authStore.state.authAction,
        authAttemptId: authStore.state.authAttemptId,
      });
      updateAuthState({
        authBusy: false,
        authAction: null,
        authActionStartedAt: 0,
      });
    }, remainingMs);
  }, [
    authState.authAction,
    authState.authBusy,
    authState.authActionStartedAt,
    authState.authStatus,
    authState.user,
    authState.authAttemptId,
    isNestedProvider,
  ]);

  return (
    isNestedProvider ? (
      <>{children}</>
    ) : (
      <AuthContext.Provider value={value}>
        {children}
        {authDiagEnabledLocal ? (
          <AuthStateDebug />
        ) : null}
      </AuthContext.Provider>
    )
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
